import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
    Layout, 
    Button, 
    message, 
    Modal,
    Checkbox,
    Input,
    Badge,
    Spin,
    Tooltip,
    Dropdown,
    Space,
    Typography,
    Upload
} from 'antd';
import { 
    InboxOutlined, 
    SendOutlined, 
    DeleteOutlined,
    MailOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    DownOutlined,
    UpOutlined,
    CalendarOutlined,
    SearchOutlined,
    EditOutlined,
    RestOutlined,
    FileTextOutlined,
    FileAddOutlined,
    FolderOpenOutlined,
    FormOutlined,
    CloseOutlined,
    MessageOutlined,
    LockOutlined,
    RollbackOutlined,
    PlusOutlined,
    DownloadOutlined,
    CheckOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    StopOutlined,
    UploadOutlined,
    CommentOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/profile/Mailbox.css';
import '../../styles/documents/DocumentRequestRecords.css';
import '../../styles/documenso/DocumensoPreview.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMailbox } from '../../hooks/useMailbox';
import FooterComponent from '../layout/Footer';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import SignaturePlacementModal from '../documenso/SignaturePlacementModal';
import DocumensoSignModal from '../documenso/DocumensoSignModal';
import { isConvertible } from '../../utility/fileTypes';

dayjs.extend(relativeTime);

const EMPTY_ARRAY = [];

const { Content } = Layout;
const { Text } = Typography;

const Mailbox = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const {
        allFolderData,
        folderCounts,
        loading,
        dataLoaded,
        refresh: refreshMailbox
    } = useMailbox();

    // Get folder from URL query param or localStorage
    const getFolderFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get('folder') || localStorage.getItem('mailbox_current_view') || 'inbox';
    };

    const [emails, setEmails] = useState([]);
    const [currentView, setCurrentView] = useState(getFolderFromUrl);
    const [selectedMessages, setSelectedMessages] = useState([]);
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
    const [expandedThreads] = useState(() => {
        const savedExpandedThreads = localStorage.getItem('mailbox_expanded_threads');
        return savedExpandedThreads ? JSON.parse(savedExpandedThreads) : [];
    });
    const [searchText, setSearchText] = useState('');
    const [filteredEmails, setFilteredEmails] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'created', direction: 'descend' });
    const [sectionView, setSectionView] = useState(() => {
        return localStorage.getItem('mailbox_section_view') || 'all';
    });
    const [previewMessage, setPreviewMessage] = useState(null);
    const [previewThread, setPreviewThread] = useState(null);
    const [listWidth, setListWidth] = useState(() => {
        return parseFloat(localStorage.getItem('mailbox_list_width')) || 55;
    });
    const [isResizingPanels, setIsResizingPanels] = useState(false);
    const containerRef = useRef(null);
    const [, setIsSearchFocused] = useState(false);
    
    // Resizable document section in preview panel (similar to DocumentPreview.js)
    const [previewColumnWidths, setPreviewColumnWidths] = useState(() => {
        const saved = localStorage.getItem('mailbox_preview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, documents: 40 };
    });
    const [isResizingPreview, setIsResizingPreview] = useState(false);
    const [threadWidthPercent, setThreadWidthPercent] = useState(() => {
        const saved = localStorage.getItem('mailbox_thread_width_percent');
        return saved ? parseFloat(saved) : 25;
    });
    const [isResizingThread, setIsResizingThread] = useState(false);
    const previewContentRef = useRef(null);
    const [isSearchSectionCollapsed, setIsSearchSectionCollapsed] = useState(() => {
        return localStorage.getItem('mailbox_search_collapsed') === 'true';
    });
    const [composeModalVisible, setComposeModalVisible] = useState(false);
    const [previewDenyModalVisible, setPreviewDenyModalVisible] = useState(false);
    const [previewDenyReason, setPreviewDenyReason] = useState('');
    const [previewDenyingFileId, setPreviewDenyingFileId] = useState(null);
    const [previewDocumentUploadedFiles, setPreviewDocumentUploadedFiles] = useState({});
    const [previewSystemSettings, setPreviewSystemSettings] = useState(null);
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [draftFileComments, setDraftFileComments] = useState({});
    const [placementModalOpen, setPlacementModalOpen] = useState(false);
    const [documensoSignModalOpen, setDocumensoSignModalOpen] = useState(false);
    const [placementSelectedFile, setPlacementSelectedFile] = useState(null);
    const [placementSelectedFileIndex, setPlacementSelectedFileIndex] = useState(0);
    const [signaturePlacementValue, setSignaturePlacementValue] = useState({});
    const [editSentFileComments, setEditSentFileComments] = useState({});
    const saveDraftCommentsTimeoutRef = useRef(null);
    const draftFileCommentsRef = useRef({});
    draftFileCommentsRef.current = draftFileComments;

    // Persist draft file comments (and signature fields for documenso) to backend
    const saveDraftCommentsFromMailbox = useCallback(async (updatedCommentsByFile, signatureFieldsToSave = null) => {
        if (!previewMessage || (previewMessage.folder !== 'drafts' && previewMessage.folder !== 'templates')) return;
        const draftId = previewMessage.unique_identifier;
        if (!draftId) return;
        const isTemplate = previewMessage.folder === 'templates';
        const isDocumenso = previewMessage.message_type === 'documenso';
        const files = isDocumenso ? (previewMessage.documenso_files || []) : (previewMessage.files || []);
        const draftFileIds = files.map((f) => f.unique_identifier);
        if (draftFileIds.length === 0) return;

        const attachmentDraftCommentsPayload = {};
        draftFileIds.forEach((fid) => {
            const list = updatedCommentsByFile[fid];
            if (Array.isArray(list) && list.length > 0) {
                attachmentDraftCommentsPayload[fid] = list.map((c) => ({
                    content: c.content,
                    page: c.page ?? 1,
                    position_x: c.position_x ?? 50,
                    position_y: c.position_y ?? 50,
                    width: c.width ?? 25,
                    height: c.height ?? 10,
                }));
            }
        });

        try {
            const params = new URLSearchParams();
            params.append('draft_id', draftId);
            params.append('subject', previewMessage.subject || '');
            params.append('message', previewMessage.body || '');
            params.append('recipient', previewMessage.recipient || '');
            params.append('cc', previewMessage.cc || '');
            params.append('bcc', previewMessage.bcc || '');
            params.append('existing_draft_file_ids', JSON.stringify(draftFileIds));
            params.append('attachment_draft_comments', JSON.stringify(attachmentDraftCommentsPayload));
            params.append('self_destruct', (previewMessage.self_destruct || false).toString());
            params.append('is_template', isTemplate.toString());
            params.append('message_type', isDocumenso ? 'documenso' : 'message');
            if (previewMessage.ttlive) {
                const ts = dayjs(previewMessage.ttlive);
                params.append('timestamp', ts.isValid() ? ts.format('YYYY-MM-DD HH:mm:ss') : '');
            }
            params.append('prevent_message_copy', (previewMessage.prevent_message_copy || false).toString());
            const sf = signatureFieldsToSave ?? signaturePlacementValueRef.current?.signatureFields;
            if (isDocumenso && sf && Object.keys(sf).length > 0) {
                params.append('signature_fields', JSON.stringify(sf));
            }

            const endpoint = isDocumenso ? '/api/docuso/save_draft/' : '/api/save_message_draft/';
            await axios.post(endpoint, params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            message.success(t('common.saved'));
        } catch (err) {
            message.error(t('messages.errors.saveFailed'));
        }
    }, [previewMessage, t]);

    const latestDraftCommentsForSaveRef = useRef({});
    const signaturePlacementValueRef = useRef({});
    signaturePlacementValueRef.current = signaturePlacementValue;

    const handleDraftFileCommentsChangeFromMailbox = useCallback((comments) => {
        if (!attachmentPreviewDoc?.draftFileId) return;
        const fid = attachmentPreviewDoc.draftFileId;
        latestDraftCommentsForSaveRef.current = { ...latestDraftCommentsForSaveRef.current, [fid]: comments };
        setDraftFileComments((prev) => ({ ...prev, [fid]: comments }));

        if (saveDraftCommentsTimeoutRef.current) clearTimeout(saveDraftCommentsTimeoutRef.current);
        saveDraftCommentsTimeoutRef.current = setTimeout(() => {
            const toSave = { ...draftFileCommentsRef.current, ...latestDraftCommentsForSaveRef.current };
            saveDraftCommentsFromMailbox(toSave);
            saveDraftCommentsTimeoutRef.current = null;
        }, 500);
    }, [attachmentPreviewDoc?.draftFileId, saveDraftCommentsFromMailbox]);

    useEffect(() => {
        return () => {
            if (saveDraftCommentsTimeoutRef.current) clearTimeout(saveDraftCommentsTimeoutRef.current);
        };
    }, []);

    // Initialize draft file comments when opening preview from mailbox (comments come from backend via file.comments)
    useEffect(() => {
        if (attachmentPreviewDoc?.draftFileId && attachmentPreviewDoc?.initialDraftComments != null) {
            const fid = attachmentPreviewDoc.draftFileId;
            const initial = attachmentPreviewDoc.initialDraftComments;
            latestDraftCommentsForSaveRef.current = { ...latestDraftCommentsForSaveRef.current, [fid]: initial };
            setDraftFileComments((prev) => {
                if (prev[fid]) return prev;
                return { ...prev, [fid]: initial };
            });
        }
    }, [attachmentPreviewDoc?.draftFileId, attachmentPreviewDoc?.initialDraftComments]);

    // Load signature_fields when opening placement modal for draft/template/sent
    useEffect(() => {
        if (!placementModalOpen || !placementSelectedFile || !previewMessage) return;
        const isDraftOrTemplate = previewMessage.folder === 'drafts' || previewMessage.folder === 'templates';
        const isSent = currentView === 'sent';
        if (!isDraftOrTemplate && !isSent) return;

        const loadSignatureFields = async () => {
            try {
                if (isDraftOrTemplate && previewMessage.message_type === 'documenso') {
                    const endpoint = previewMessage.folder === 'templates'
                        ? `/api/docuso/get_template/${previewMessage.unique_identifier}/`
                        : `/api/docuso/get_draft/${previewMessage.unique_identifier}/`;
                    const res = await axios.get(endpoint);
                    const draft = res.data?.draft;
                    const sigFields = draft?.signature_fields ?? res.data?.signature_fields;
                    if (sigFields && Object.keys(sigFields).length > 0) {
                        setSignaturePlacementValue({ signatureFields: sigFields });
                    }
                    const files = draft?.files ?? res.data?.documenso_files ?? previewMessage.documenso_files ?? [];
                    files.forEach((f) => {
                        if (Array.isArray(f.comments) && f.comments.length > 0) {
                            const fid = String(f.unique_identifier);
                            setDraftFileComments((prev) => (prev[fid] ? prev : { ...prev, [fid]: f.comments }));
                        }
                    });
                } else if (isSent && previewMessage.message_type === 'documenso') {
                    const res = await axios.get(`/api/docuso/for_edit/${previewMessage.unique_identifier}/`);
                    if (res.data?.signature_fields && Object.keys(res.data.signature_fields).length > 0) {
                        setSignaturePlacementValue({ signatureFields: res.data.signature_fields });
                    }
                    const files = res.data?.documenso_files || [];
                    const commentsMap = {};
                    files.forEach((f) => {
                        if (Array.isArray(f.comments) && f.comments.length > 0) {
                            commentsMap[String(f.unique_identifier)] = f.comments.map((c) => ({
                                ...c,
                                unique_identifier: c.unique_identifier || `doc-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            }));
                        }
                    });
                    setEditSentFileComments(commentsMap);
                }
            } catch (err) {
                console.error('Load signature fields failed:', err);
            }
        };
        loadSignatureFields();
    }, [placementModalOpen, placementSelectedFile, previewMessage, currentView]);

    const saveSignatureFieldsTimeoutRef = useRef(null);
    const handleSignaturePlacementChange = useCallback((newValue) => {
        setSignaturePlacementValue(newValue);
        if (!previewMessage || (previewMessage.folder !== 'drafts' && previewMessage.folder !== 'templates')) return;
        if (saveSignatureFieldsTimeoutRef.current) clearTimeout(saveSignatureFieldsTimeoutRef.current);
        saveSignatureFieldsTimeoutRef.current = setTimeout(() => {
            saveDraftCommentsFromMailbox(draftFileCommentsRef.current, newValue?.signatureFields);
            saveSignatureFieldsTimeoutRef.current = null;
        }, 500);
    }, [previewMessage, saveDraftCommentsFromMailbox]);

    useEffect(() => {
        return () => {
            if (saveSignatureFieldsTimeoutRef.current) clearTimeout(saveSignatureFieldsTimeoutRef.current);
        };
    }, []);

    const getCombinedEmails = useCallback(() => {
        if (!dataLoaded) return [];
        
        let data = [];
        
        if (sectionView === 'all') {
            const messageData = allFolderData[currentView] || [];
            const documentKey = `${currentView}_documents`;
            const documentData = allFolderData[documentKey] || [];
            data = [...messageData, ...documentData];
        } else if (sectionView === 'messages') {
            const messageData = allFolderData[currentView] || [];
            data = messageData.filter(item => item.message_type !== 'documenso');
        } else if (sectionView === 'documents') {
            const documentKey = `${currentView}_documents`;
            data = allFolderData[documentKey] || [];
        } else if (sectionView === 'signature_requests') {
            const messageData = allFolderData[currentView] || [];
            data = messageData.filter(item => item.item_type !== 'document' && item.message_type === 'documenso');
        }
        
        return data;
    }, [dataLoaded, sectionView, allFolderData, currentView]);

    const stripHtmlTags = useCallback((html) => {
        if (!html) return '';
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }, []);

    const getCommentsFromPreviewMessage = useCallback((msg, draftFileId) => {
        if (!msg || !draftFileId) return undefined;
        const fromFiles = msg.files?.find((f) => f.unique_identifier === draftFileId)?.comments;
        if (Array.isArray(fromFiles)) return fromFiles;
        const fromDocumenso = msg.documenso_files?.find((f) => f.unique_identifier === draftFileId)?.comments;
        if (Array.isArray(fromDocumenso)) return fromDocumenso;
        return undefined;
    }, []);

    useEffect(() => {
        const combinedData = getCombinedEmails();
        setEmails(combinedData);
    }, [getCombinedEmails]);

    // Sync previewMessage when mailbox data refreshes (realtime: files, comments, documenso status, etc.)
    useEffect(() => {
        if (!previewMessage?.unique_identifier) return;
        const combinedData = getCombinedEmails();
        const flatData = combinedData.flatMap(item => {
            if (item.replies?.length) return [item, ...item.replies];
            return [item];
        });
        const updated = flatData.find(item => item.unique_identifier === previewMessage.unique_identifier);
        if (updated && JSON.stringify(updated) !== JSON.stringify(previewMessage)) {
            setPreviewMessage(updated);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFolderData]);

    // Clear preview uploaded files when switching to a different document
    useEffect(() => {
        setPreviewDocumentUploadedFiles({});
    }, [previewMessage?.unique_identifier]);

    // Sync currentView with URL query param when it changes (from sidemenu)
    useEffect(() => {
        const folderFromUrl = getFolderFromUrl();
        if (folderFromUrl !== currentView) {
            setCurrentView(folderFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    useEffect(() => {
        // Apply search filter whenever emails or searchText changes
            if (searchText.trim() === '') {
            setFilteredEmails(emails);
        } else {
            const searchTerm = searchText.toLowerCase();
            if (currentView === 'sent' || currentView === 'drafts' || currentView === 'templates' || currentView === 'deleted') {
                // Filter flat list emails/documents
                const filtered = emails.filter(item => {
                    return (
                        (item.recipient && item.recipient.toLowerCase().includes(searchTerm)) ||
                        (item.sender && item.sender.toLowerCase().includes(searchTerm)) ||
                        (item.subject && item.subject.toLowerCase().includes(searchTerm)) ||
                        (item.body && item.body.toLowerCase().includes(searchTerm)) ||
                        (item.preview && item.preview.toLowerCase().includes(searchTerm)) ||
                        (item.created_at && item.created_at.toLowerCase().includes(searchTerm)) ||
                        (item.ttlive && item.ttlive.toLowerCase().includes(searchTerm))
                    );
                });
                setFilteredEmails(filtered);
            } else {
                // Filter inbox threads (includes both threaded emails and documents)
                const filtered = emails.filter(item => {
                    // Check if this is a threaded email (has replies array) or a flat document
                    const isThreadedEmail = item.replies !== undefined;
                    
                    if (!isThreadedEmail) {
                        // Handle flat items in inbox (documents when "all" is selected)
                        return (
                            (item.sender && item.sender.toLowerCase().includes(searchTerm)) ||
                            (item.subject && item.subject.toLowerCase().includes(searchTerm)) ||
                            (item.body && item.body.toLowerCase().includes(searchTerm)) ||
                            (item.preview && item.preview.toLowerCase().includes(searchTerm)) ||
                            (item.created_at && item.created_at.toLowerCase().includes(searchTerm)) ||
                            (item.ttlive && item.ttlive.toLowerCase().includes(searchTerm))
                        );
                    }
                    
                    // For threaded emails, check main email
                    const mainMatches = (item.sender && item.sender.toLowerCase().includes(searchTerm)) ||
                        (item.subject && item.subject.toLowerCase().includes(searchTerm)) ||
                        (item.body && item.body.toLowerCase().includes(searchTerm)) ||
                        (item.preview && item.preview.toLowerCase().includes(searchTerm)) ||
                        (item.created_at && item.created_at.toLowerCase().includes(searchTerm)) ||
                        (item.ttlive && item.ttlive.toLowerCase().includes(searchTerm));
                    
                    // Check if any replies match the search
                    let repliesMatch = false;
                    if (hasReplies(item)) {
                        repliesMatch = item.replies.some(reply => 
                            (reply.sender && reply.sender.toLowerCase().includes(searchTerm)) ||
                            (reply.subject && reply.subject.toLowerCase().includes(searchTerm)) ||
                            (reply.body && reply.body.toLowerCase().includes(searchTerm)) ||
                            (reply.preview && reply.preview.toLowerCase().includes(searchTerm)) ||
                            (reply.created_at && reply.created_at.toLowerCase().includes(searchTerm)) ||
                            (reply.ttlive && reply.ttlive.toLowerCase().includes(searchTerm))
                        );
                    }
                    
                    return mainMatches || repliesMatch;
                });
                setFilteredEmails(filtered);
            }
        }
    }, [emails, searchText, currentView]);

    // Save the current view to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('mailbox_current_view', currentView);
    }, [currentView]);

    // Save the selected record per folder for restore on page load
    useEffect(() => {
        if (previewMessage?.unique_identifier && currentView) {
            localStorage.setItem(`mailbox_preview_record_${currentView}`, previewMessage.unique_identifier);
        }
    }, [previewMessage?.unique_identifier, currentView]);

    // Save expandedThreads to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('mailbox_expanded_threads', JSON.stringify(expandedThreads));
    }, [expandedThreads]);

    useEffect(() => {
        localStorage.setItem('mailbox_section_view', sectionView);
    }, [sectionView]);

    // Save list width to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('mailbox_list_width', listWidth.toString());
    }, [listWidth]);

    // Save search section collapsed state to localStorage
    useEffect(() => {
        localStorage.setItem('mailbox_search_collapsed', isSearchSectionCollapsed.toString());
    }, [isSearchSectionCollapsed]);

    // Handle panel resize mouse events
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingPanels || !containerRef.current) return;
            
            const containerRect = containerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            
            // Calculate percentage (constrain between 30% and 80%, matching attachments separator)
            let newListWidth = (mouseX / containerWidth) * 100;
            newListWidth = Math.max(30, Math.min(80, newListWidth));
            
            setListWidth(newListWidth);
        };

        const handleMouseUp = () => {
            if (isResizingPanels) {
                setIsResizingPanels(false);
                document.body.classList.remove('mailbox-resizing-panels');
            }
        };

        if (isResizingPanels) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('mailbox-resizing-panels');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('mailbox-resizing-panels');
        };
    }, [isResizingPanels]);

    // Handle preview column resize mouse events (for document section)
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingPreview || !previewContentRef.current) return;
            
            const containerRect = previewContentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            
            // Constrain body width between 30% and 80%
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const documentsWidth = 100 - bodyWidth;
            
            const newWidths = { body: bodyWidth, documents: documentsWidth };
            setPreviewColumnWidths(newWidths);
            localStorage.setItem('mailbox_preview_column_widths', JSON.stringify(newWidths));
        };

        const handleMouseUp = () => {
            if (isResizingPreview) {
                setIsResizingPreview(false);
                document.body.classList.remove('mailbox-preview-resizing');
            }
        };

        if (isResizingPreview) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('mailbox-preview-resizing');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('mailbox-preview-resizing');
        };
    }, [isResizingPreview]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingThread || !previewContentRef.current) return;
            const containerRect = previewContentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const threadWidth = containerWidth - mouseX;
            let newPercent = (threadWidth / containerWidth) * 100;
            newPercent = Math.max(15, Math.min(45, newPercent));
            setThreadWidthPercent(newPercent);
            localStorage.setItem('mailbox_thread_width_percent', newPercent.toString());
        };
        const handleMouseUp = () => {
            if (isResizingThread) {
                setIsResizingThread(false);
                document.body.classList.remove('mailbox-thread-resizing');
            }
        };
        if (isResizingThread) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('mailbox-thread-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('mailbox-thread-resizing');
        };
    }, [isResizingThread]);

    const handleRecover = async () => {
        if (selectedMessages.length === 0) return;

        Modal.confirm({
            title: t('mailbox.recoverMessages'),
            content: t('mailbox.recoverConfirm', { count: selectedMessages.length }),
            okText: t('mailbox.recover'),
            onOk: async () => {
                try {
                    // Recover each message individually using the recover endpoint
                    const recoverPromises = selectedMessages.map(messageId => 
                        axios.post(`/api/message/recover/${messageId}/`)
                    );
                    
                    await Promise.all(recoverPromises);
                    message.success(t('mailbox.messagesRecovered'));
                    setSelectedMessages([]);
                    refreshMailbox();
                } catch (error) {
                    // Show the specific error message from the backend
                    const errorMessage = error.response?.data?.status || 
                                        error.response?.data?.error ||
                                        t('mailbox.failedToRecover');
                    message.error(errorMessage);
                }
            }
        });
    };

    const handleDelete = async (messageId = null) => {
        // Use provided messageId or fall back to selectedMessages
        const messagesToDelete = messageId ? [messageId] : selectedMessages;
        if (messagesToDelete.length === 0) return;

        // Set the appropriate endpoint and confirmation message based on current view
        const isTrashFolder = currentView === 'deleted';
        const isDraftsFolder = currentView === 'drafts';
        const isTemplatesFolder = currentView === 'templates';
        
        const confirmTitle = isTrashFolder ? 
            t('mailbox.permanentDeleteMessages') : 
            t('mailbox.deleteMessages');
        const confirmContent = isTrashFolder ? 
            t('mailbox.permanentDeleteConfirm', { count: messagesToDelete.length }) : 
            t('mailbox.deleteConfirm', { count: messagesToDelete.length });
        const okText = isTrashFolder ? t('mailbox.permanentDelete') : t('mailbox.delete');

        Modal.confirm({
            title: confirmTitle,
            content: confirmContent,
            okText: okText,
            okType: 'danger',
            onOk: async () => {
                try {
                    let deletePromises;
                    
                    if (isDraftsFolder || isTemplatesFolder) {
                        // For drafts and templates, batch delete by item type
                        const messageDraftIds = [];
                        const documentDraftIds = [];
                        
                        // Separate items by type
                        messagesToDelete.forEach(draftId => {
                            const selectedItem = filteredEmails.find(item => item.unique_identifier === draftId);
                            if (selectedItem && selectedItem.item_type === 'document') {
                                documentDraftIds.push(draftId);
                            } else {
                                messageDraftIds.push(draftId);
                            }
                        });
                        
                        deletePromises = [
                            ...messageDraftIds.map(id => axios.delete(`/api/delete_message_draft/${id}/`)),
                            ...documentDraftIds.map(id => axios.delete(`/api/delete_document_draft/${id}/`))
                        ];
                    } else {
                        // For all other folders (sent, inbox, documents, deleted), use appropriate delete endpoint
                        // Prepare the data with item IDs and their types
                        const itemsToDelete = messagesToDelete.map(itemId => {
                            const selectedItem = filteredEmails.find(item => item.unique_identifier === itemId);
                            return {
                                id: itemId,
                                item_type: selectedItem ? selectedItem.item_type : 'message'
                            };
                        });
                        
                        // Use appropriate delete endpoint based on folder
                        const deleteEndpoint = isTrashFolder ? '/api/profile/mailbox/trash/' : '/api/profile/mailbox/delete/';
                        deletePromises = [axios.post(deleteEndpoint, {
                            items: itemsToDelete
                        })];
                    }
                    
                    await Promise.all(deletePromises);
                    
                    const successMessage = (isDraftsFolder || isTemplatesFolder) ? 
                        (isTemplatesFolder ? t('mailbox.templatesDeleted') || 'Templates deleted' : t('mailbox.draftsDeleted')) : 
                        (isTrashFolder ? t('mailbox.messagesPermanentlyDeleted') : t('mailbox.messagesMovedToTrash'));
                    
                    message.success(successMessage);
                    setSelectedMessages([]);
                    refreshMailbox();
                } catch (error) {
                    const errorMessage = error.response?.data?.status || 
                                        error.response?.data?.error ||
                                        t('mailbox.failedToDelete');
                    message.error(errorMessage);
                }
            }
        });
    };

    const handleEdit = async (messageId = null) => {
        // Use provided messageId or fall back to selectedMessages
        const idToUse = messageId || (selectedMessages.length === 1 ? selectedMessages[0] : null);
        if (!idToUse) return;
        
        try {
            // Find the selected item to check its type
            const selectedItem = filteredEmails.find(item => item.unique_identifier === idToUse);
            
            let response;
            if (selectedItem && selectedItem.item_type === 'document') {
                // Use document edit endpoint for documents
                response = await axios.post(`/api/document/edit/${idToUse}/`);
                
                if (response.data.can_edit_all) {
                    // Show success message for moving to drafts
                    message.success(t('documentPreview.movedToDrafts') || 'Document moved to drafts');
                    // For unread documents, navigate to the draft edit form
                    navigate(`/document-compose?draft_id=${response.data.draft_id}`);
            } else {
                    // For read documents, navigate with limited edit parameters
                    const currentValues = response.data.current_values;
                    const documentData = response.data.document_data || {};
                    
                    // Store the limited edit data in localStorage with all document data
                    localStorage.setItem('limitedEditDocument', JSON.stringify({
                        documentId: response.data.document_id,
                        self_destruct: currentValues.self_destruct,
                        ttlive: currentValues.ttlive,
                        limitedEdit: true,
                        documentData: documentData
                    }));
                    
                    // Navigate to the document-compose page with a special parameter
                    navigate(`/document-compose?limited_edit=${response.data.document_id}`);
                }
            } else {
                const isSignatureRequest = selectedItem?.message_type === 'documenso';
                if (isSignatureRequest) {
                    navigate(`/signature-request?edit_sent=${idToUse}`);
                } else {
                    response = await axios.post(`/api/message/edit/${idToUse}/`);
                    if (response.data.can_edit_all) {
                        message.success(t('mailbox.convertedToDraft'));
                        navigate(`/message-compose?draft_id=${response.data.draft_id}`);
                    } else {
                        const currentValues = response.data.current_values;
                        const messageData = response.data.message_data || {};
                        localStorage.setItem('limitedEditMessage', JSON.stringify({
                            messageId: response.data.message_id,
                            self_destruct: currentValues.self_destruct,
                            ttlive: currentValues.ttlive,
                            limitedEdit: true,
                            messageData: messageData
                        }));
                        navigate(`/message-compose?limited_edit=${response.data.message_id}`);
                    }
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error ||
                                t('mailbox.failedToEdit');
            message.error(errorMessage);
        }
    };

    const handleEditDraft = (draftId = null) => {
        // Use provided draftId or fall back to selectedMessages
        const idToUse = draftId || (selectedMessages.length === 1 ? selectedMessages[0] : null);
        if (!idToUse) return;
        
        // Find the selected item - use access_link from model when available (handles signature requests)
        const selectedItem = filteredEmails.find(item => item.unique_identifier === idToUse);
        
        // Clear any existing draft cache to ensure we load the selected draft properly
        localStorage.removeItem('currentDraftId');
        
        if (selectedItem && selectedItem.item_type === 'document') {
            localStorage.setItem('editSource', 'drafts');
            navigate(`/document-compose?draft_id=${idToUse}`);
        } else if (selectedItem?.access_link && (selectedItem.access_link.includes('/signature-request') || selectedItem.access_link.includes('/message-compose') || selectedItem.access_link.includes('/document-compose'))) {
            // Use access_link from model (covers both regular and signature-request drafts)
            localStorage.setItem('editSource', 'drafts');
            navigate(selectedItem.access_link);
        } else {
            // Fallback if access_link not present
            localStorage.setItem('editSource', 'drafts');
            navigate(`/message-compose?draft_id=${idToUse}`);
        }
    };

    const handleEditTemplate = (templateId = null) => {
        // Use provided templateId or fall back to selectedMessages
        const idToUse = templateId || (selectedMessages.length === 1 ? selectedMessages[0] : null);
        if (!idToUse) return;
        
        // Find the selected item - use access_link from model when available (handles signature requests)
        const selectedItem = filteredEmails.find(item => item.unique_identifier === idToUse);
        
        // Clear any existing draft cache to ensure we load the selected template properly
        localStorage.removeItem('currentDraftId');
        
        if (selectedItem && selectedItem.item_type === 'document') {
            localStorage.setItem('editSource', 'templates');
            navigate(`/document-compose?template_id=${idToUse}&folder=templates`);
        } else if (selectedItem?.access_link && (selectedItem.access_link.includes('/signature-request') || selectedItem.access_link.includes('/message-compose') || selectedItem.access_link.includes('/document-compose'))) {
            // Use access_link from model (covers both regular and signature-request templates)
            localStorage.setItem('editSource', 'templates');
            navigate(selectedItem.access_link);
        } else {
            // Fallback if access_link not present
            localStorage.setItem('editSource', 'templates');
            navigate(`/message-compose?template_id=${idToUse}&folder=templates`);
        }
    };

    // Extract the identifier for reply/forward: inbox uses receiver_access_identifier from /link/{id}/,
    // sent/deleted use unique_identifier from /link/preview/{id}/. Backend supports both.
    const getReplyIdentifier = (msg) => {
        if (!msg) return null;
        if (msg.access_link) {
            const match = msg.access_link.match(/\/([a-f0-9-]{36})\/?$/i);
            if (match) return match[1];
        }
        return msg.access_identifier || msg.unique_identifier;
    };

    const handleReply = (msg) => {
        const id = getReplyIdentifier(msg);
        if (!id) return;
        const replyUrl = `/link/reply/${id}?action=reply`;
        navigate(replyUrl, { state: { preserveViews: true } });
    };

    const handleReplyAll = (msg) => {
        const id = getReplyIdentifier(msg);
        if (!id) return;
        const replyAllUrl = `/link/reply/${id}?reply_all=true&action=reply_all`;
        navigate(replyAllUrl, { state: { preserveViews: true } });
    };

    const handleForward = (msg) => {
        const id = getReplyIdentifier(msg);
        if (!id) return;
        const forwardUrl = `/link/reply/${id}?forward=true&action=forward`;
        navigate(forwardUrl, { state: { preserveViews: true } });
    };

    // Same logic as MessageView: check if there are multiple recipients
    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        if (typeof recipients === 'string') {
            const emailList = recipients.split(/[,;\s]+/).map(email => email.trim()).filter(email => email !== '');
            return emailList.length > 1;
        }
        if (Array.isArray(recipients)) return recipients.length > 1;
        return false;
    };

    // Same logic as MessageView: hide Reply All when viewer is only in BCC
    const isViewerOnlyInBcc = (msg) => {
        if (!msg || !user?.email) return false;
        const bccList = msg.bcc ? msg.bcc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const isInBcc = bccList.includes(user.email.toLowerCase());
        const recipientList = msg.recipient ? msg.recipient.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const ccList = msg.cc ? msg.cc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const isInRecipientOrCc = recipientList.includes(user.email.toLowerCase()) || ccList.includes(user.email.toLowerCase());
        return isInBcc && !isInRecipientOrCc;
    };

    const handlePreviewDownload = async (attachmentId, fileName, isDocumenso = false) => {
        try {
            const downloadUrl = isDocumenso ? `/api/docuso/download/${attachmentId}/` : `/api/messages/download/${attachmentId}/`;
            const response = await axios.get(downloadUrl, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'download');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handlePreviewDraftFileDownload = async (draftFileId, fileName) => {
        try {
            const response = await axios.get(`/api/drafts/files/${draftFileId}/`, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'download');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handlePreviewDownloadAll = async () => {
        if (!previewMessage) return;
        try {
            let downloadUrl;
            if (previewMessage.item_type === 'document') {
                downloadUrl = `/api/documents/${previewMessage.unique_identifier}/download-all/`;
            } else if (previewMessage.message_type === 'documenso' || previewMessage.documenso_files?.length > 0) {
                downloadUrl = `/api/docuso/${previewMessage.unique_identifier}/download-all/`;
            } else if (previewMessage.folder === 'drafts' || previewMessage.folder === 'templates') {
                downloadUrl = `/api/drafts/${previewMessage.unique_identifier}/download-all/`;
            } else {
                downloadUrl = `/api/messages/${previewMessage.unique_identifier}/download-all/`;
            }
            const response = await axios.get(downloadUrl, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', previewMessage.item_type === 'document' ? 'documents.zip' : 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    // Fetch system settings when receiver views document (for upload validation)
    useEffect(() => {
        if (previewMessage?.item_type === 'document' && previewMessage.sender !== user?.email) {
            axios.get('/api/system-settings/').then(res => {
                setPreviewSystemSettings(res.data);
            }).catch(() => {});
        } else {
            setPreviewSystemSettings(null);
        }
    }, [previewMessage?.item_type, previewMessage?.sender, user?.email]);

    const handlePreviewDocumentDownload = async (documentFileId, fileName) => {
        try {
            const response = await axios.get(`/api/documents/download/${documentFileId}/`, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', fileName || 'document');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            message.error(t('documentPreview.errors.downloadFailed'));
        }
    };

    const handlePreviewUploadDocument = (file, docFile) => {
        if (docFile.document_status === 'approved' || (docFile.document_name && docFile.document_status === 'pending')) {
            message.warning(docFile.document_status === 'pending'
                ? t('documents.view.pendingCannotModify')
                : t('documents.view.approvedCannotModify'));
            return false;
        }
        if (docFile.return_type !== '*') {
            const allowedTypes = docFile.return_type.split(',').map(ext => ext.trim().toLowerCase());
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const fileType = file.type?.toLowerCase() || '';
            const isValid = allowedTypes.some(type =>
                type === fileExtension || type === fileType || type.includes(fileExtension)
            );
            if (!isValid) {
                message.error(t('documents.view.invalidFileType', { expected: docFile.return_type, received: fileExtension }));
                return false;
            }
        }
        if (previewSystemSettings?.max_upload_size) {
            const maxBytes = parseInt(previewSystemSettings.max_upload_size) * 1024 * 1024;
            if (file.size > maxBytes) {
                message.error(t('documents.view.fileTooLarge', { maxSize: previewSystemSettings.max_upload_size }));
                return false;
            }
        }
        setPreviewDocumentUploadedFiles(prev => ({
            ...prev,
            [docFile.unique_identifier]: { file, name: file.name, type: file.type, size: file.size }
        }));
        message.success(t('documents.view.fileSelectedSuccessfully'));
        return false;
    };

    const handlePreviewRemoveFile = (documentFileId) => {
        setPreviewDocumentUploadedFiles(prev => {
            const next = { ...prev };
            delete next[documentFileId];
            return next;
        });
        message.success(t('documents.view.fileRemovedSuccessfully'));
    };

    const handlePreviewSendDocuments = async () => {
        const entries = Object.entries(previewDocumentUploadedFiles);
        if (entries.length === 0) {
            message.warning(t('documents.view.noFilesToSend'));
            return;
        }
        if (!previewMessage?.unique_identifier) {
            message.error(t('documents.view.errorDocumentNotLoaded'));
            return;
        }
        try {
            const formData = new FormData();
            entries.forEach(([docFileId, data]) => formData.append(`file_${docFileId}`, data.file));
            await axios.post(`/api/save_requested_documents/${previewMessage.unique_identifier}/`, formData);
            message.success(t('documents.view.documentsSent'));
            setPreviewDocumentUploadedFiles({});
            refreshMailbox();
        } catch (err) {
            message.error(err.response?.data?.error || t('documents.view.errorSendingDocuments'));
        }
    };

    const handlePreviewApproveDocument = async (documentFileId) => {
        try {
            await axios.post(`/api/approve_deny_document_file/${documentFileId}/`, { action: 'approved' });
            message.success(t('documentPreview.documentApproved'));
            refreshMailbox();
        } catch (err) {
            message.error(err.response?.data?.error || t('documentPreview.errors.approveFailed'));
        }
    };

    const handlePreviewDenyDocument = (documentFileId) => {
        setPreviewDenyingFileId(documentFileId);
        setPreviewDenyReason('');
        setPreviewDenyModalVisible(true);
    };

    const handlePreviewConfirmDeny = async () => {
        if (!previewDenyReason.trim()) {
            message.error(t('documentPreview.denyReasonRequired'));
            return;
        }
        try {
            await axios.post(`/api/approve_deny_document_file/${previewDenyingFileId}/`, {
                action: 'denied',
                deny_reason: previewDenyReason
            });
            message.success(t('documentPreview.documentDenied'));
            setPreviewDenyModalVisible(false);
            setPreviewDenyReason('');
            setPreviewDenyingFileId(null);
            refreshMailbox();
        } catch (err) {
            message.error(err.response?.data?.error || t('documentPreview.errors.denyFailed'));
        }
    };

    const handlePreviewCancelDeny = () => {
        setPreviewDenyModalVisible(false);
        setPreviewDenyReason('');
        setPreviewDenyingFileId(null);
    };

    // Check if an email thread has replies
    const hasReplies = (thread) => {
        return thread && thread.replies && Array.isArray(thread.replies) && thread.replies.length > 0;
    };

    // Calculate all message IDs in a thread or for flat list views just get all message IDs
    const getAllMessageIds = (item) => {
        // Start with the main item's ID
        const ids = [item.unique_identifier];
        
        // If it has replies, add those IDs too
        if (hasReplies(item)) {
            item.replies.forEach(reply => {
                ids.push(reply.unique_identifier);
            });
        }
        
        return ids;
    };

    // Sort data based on current sort configuration
    const sortData = React.useCallback((data) => {
        if (!sortConfig.key || !sortConfig.direction) {
            return data;
        }

        return [...data].sort((a, b) => {
            let aValue, bValue;

            // All items are now flat - just access their properties directly
                switch (sortConfig.key) {
                    case 'to':
                        aValue = a.recipient || '';
                        bValue = b.recipient || '';
                        break;
                    case 'from':
                        aValue = a.sender || '';
                        bValue = b.sender || '';
                        break;
                    case 'subject':
                        aValue = a.subject || '';
                        bValue = b.subject || '';
                        break;
                    case 'self_destruct':
                        aValue = a.self_destruct ? 1 : 0;
                        bValue = b.self_destruct ? 1 : 0;
                        break;
                    case 'created':
                        aValue = new Date(a.created_at || 0);
                        bValue = new Date(b.created_at || 0);
                        break;
                    case 'expires':
                        aValue = new Date(a.ttlive || 0);
                        bValue = new Date(b.ttlive || 0);
                        break;
                    case 'read':
                        // Sort by read status: unread first (false) then read (true) for ascend
                        aValue = a.has_been_read ? 1 : 0;
                        bValue = b.has_been_read ? 1 : 0;
                        break;
                    default:
                        aValue = a[sortConfig.key] || '';
                        bValue = b[sortConfig.key] || '';
            }

            // Handle different data types
            if (aValue instanceof Date && bValue instanceof Date) {
                return sortConfig.direction === 'ascend' ? aValue - bValue : bValue - aValue;
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortConfig.direction === 'ascend' ? aValue - bValue : bValue - aValue;
            } else {
                // String comparison
                const aStr = String(aValue).toLowerCase();
                const bStr = String(bValue).toLowerCase();
                
                if (sortConfig.direction === 'ascend') {
                    return aStr.localeCompare(bStr);
                } else {
                    return bStr.localeCompare(aStr);
                }
            }
        });
    }, [sortConfig]);

    // Auto-select record when folder/section has records: restore last selected from localStorage, or first if not found
    useEffect(() => {
        if (loading || !dataLoaded || filteredEmails.length === 0) return;
        const sorted = sortData(filteredEmails);
        const first = sorted[0];
        if (!first) return;
        const previewInList = previewMessage && sorted.some(item => {
            if (item.unique_identifier === previewMessage.unique_identifier) return true;
            if (hasReplies(item) && item.replies?.some(r => r.unique_identifier === previewMessage.unique_identifier)) return true;
            return false;
        });
        if (!previewMessage || !previewInList) {
            const savedId = localStorage.getItem(`mailbox_preview_record_${currentView}`);
            let toPreview = first;
            let previewThreadVal = hasReplies(first) ? first : null;
            if (savedId) {
                const found = sorted.find(item => {
                    if (item.unique_identifier === savedId) return true;
                    if (hasReplies(item) && item.replies?.some(r => r.unique_identifier === savedId)) return true;
                    return false;
                });
                if (found) {
                    const isReply = found.replies?.some(r => r.unique_identifier === savedId);
                    if (isReply) {
                        toPreview = found.replies.find(r => r.unique_identifier === savedId);
                        previewThreadVal = found;
                    } else {
                        toPreview = found;
                        previewThreadVal = hasReplies(found) ? found : null;
                    }
                }
            }
            setPreviewMessage(toPreview);
            setPreviewThread(previewThreadVal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, dataLoaded, filteredEmails, currentView, sectionView, sortData]);

    // Get all message IDs from all threads or emails (using sorted data)
    const allThreadMessages = React.useMemo(() => {
        const allIds = [];
        const sortedEmails = sortData(filteredEmails);
        
        sortedEmails.forEach(threadOrEmail => {
            allIds.push(...getAllMessageIds(threadOrEmail));
        });
        
        return allIds;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredEmails, sortConfig, sortData]);

    const handleSelectAll = () => {
        if (isAllSelected) {
            // Deselect all messages
            setSelectedMessages([]);
        } else {
            // Select all messages
            setSelectedMessages(allThreadMessages);
        }
    };

    const isAllSelected = allThreadMessages.length > 0 && 
        allThreadMessages.every(id => selectedMessages.includes(id));
    const isIndeterminate = selectedMessages.length > 0 && 
        !isAllSelected;

    const handleMessageClick = (messageItem, isReply, e) => {
        e.stopPropagation(); // Prevent event bubbling
        
        const messageId = messageItem.unique_identifier;
        
        // Get current data based on view to find record index
        const currentData = filteredEmails.flatMap(item => {
            // Start with the main item
            const messages = [item];
            // If it has replies, add them
            if (hasReplies(item)) {
                messages.push(...item.replies);
                    }
                    return messages;
            });
        
        const recordIndex = currentData.findIndex(item => item.unique_identifier === messageId);
        
        if (e.ctrlKey || e.metaKey) {
            // Ctrl+click: Toggle selection
            if (selectedMessages.includes(messageId)) {
                // Deselect
                setSelectedMessages(prev => prev.filter(id => id !== messageId));
            } else {
                // Select
                setSelectedMessages(prev => [...prev, messageId]);
                setLastSelectedIndex(recordIndex);
            }
            return;
        } else if (e.shiftKey) {
            // Shift+click: Range selection
            if (selectedMessages.length > 0 && lastSelectedIndex !== null) {
                // Range selection: select from last selected to current
                const startIndex = Math.min(lastSelectedIndex, recordIndex);
                const endIndex = Math.max(lastSelectedIndex, recordIndex);
                const rangeIds = currentData.slice(startIndex, endIndex + 1).map(item => item.unique_identifier);
                
                setSelectedMessages(prev => {
                    const newSelection = [...prev];
                    rangeIds.forEach(id => {
                        if (!newSelection.includes(id)) {
                            newSelection.push(id);
                        }
                    });
                    return newSelection;
                });
            } else {
                // No current selection or no last index, just select this one
                setSelectedMessages([messageId]);
                setLastSelectedIndex(recordIndex);
            }
            return;
        }
        
        // Regular click: Open message (original functionality)
        if (!messageItem.access_link) {
            // Fallback in case access_link is not available
            message.error(t('mailbox.unableToAccess'));
            return;
        }

        const accessUrl = messageItem.access_link;

        // Function to proceed with navigation
        const proceedWithNavigation = () => {
            // Clear any existing draft cache for message, document, and signature-request drafts
            if (accessUrl.includes('/message-compose?draft_id=') || accessUrl.includes('/document-compose?draft_id=') || accessUrl.includes('/signature-request?draft_id=')) {
                localStorage.removeItem('currentDraftId');
            }
            
            // Remove any existing X-Preserve-Views from sessionStorage
            sessionStorage.removeItem('X-Preserve-Views');
            
            // Navigate with explicit state to NOT preserve views
            navigate(accessUrl, { state: { preserveViews: false } });
        };

        proceedWithNavigation();
    };

    const handleViewChange = (view) => {
        setCurrentView(view);
        setSectionView('all'); // All items selected by default when selecting a folder
        setSelectedMessages([]);
        setLastSelectedIndex(null);
        setSortConfig({ key: 'created', direction: 'descend' }); // Reset to default sort when changing views
        setPreviewMessage(null); // Clear preview when changing views
        setPreviewThread(null); // Clear preview thread when changing views
    };

    const handleSectionChange = (value) => {
        setSectionView(value);
        setSelectedMessages([]);
        setLastSelectedIndex(null);
        setPreviewMessage(null);
        setPreviewThread(null);
    };

    // Render content based on current view using card list
    const renderMailContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            );
        }
        
        if (filteredEmails.length === 0 && searchText) {
            return <div className="mailbox-empty">{t('mailbox.noMatchingMessages')}</div>;
        }
        
        if (filteredEmails.length === 0) {
            return <div className="mailbox-empty">{t('mailbox.noMessages')}</div>;
        }
        
        const sortedEmails = sortData(filteredEmails);
        
        return (
            <div className="mailbox-list">
                {sortedEmails.map((item) => {
                    // All items are now directly message objects
                    const messageItem = item;
                    const hasRepliesFlag = hasReplies(item);
                    
                    // Check if this thread is being previewed (either the original or any reply)
                    const isThreadPreviewed = previewMessage && (
                        previewMessage.unique_identifier === messageItem.unique_identifier ||
                        (hasRepliesFlag && item.replies?.some(reply => reply.unique_identifier === previewMessage.unique_identifier))
                    );

    return (
                        <div key={messageItem.unique_identifier}>
                            <div 
                                className={`mailbox-card ${selectedMessages.includes(messageItem.unique_identifier) ? 'mailbox-card-selected' : ''} ${isThreadPreviewed ? 'mailbox-card-preview-selected' : ''}`}
                                data-item-type={messageItem.item_type || 'message'}
                                onClick={(e) => {
                                    // Don't do anything if clicking checkbox or action buttons
                                    if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.mailbox-card-action-btn')) {
                                        return;
                                    }
                                    
                                    // If Ctrl/Cmd is held, toggle checkbox selection
                                    if (e.ctrlKey || e.metaKey) {
                                        e.stopPropagation();
                                        setSelectedMessages(prev => 
                                            prev.includes(messageItem.unique_identifier)
                                                ? prev.filter(id => id !== messageItem.unique_identifier)
                                                : [...prev, messageItem.unique_identifier]
                                        );
                                        return;
                                    }
                                    
                                    // Otherwise, set as preview
                                    setPreviewMessage(messageItem);
                                    // Store the parent thread if it has replies
                                    setPreviewThread(hasRepliesFlag ? item : null);
                                }}
                                onDoubleClick={(e) => {
                                    if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.mailbox-card-action-btn')) return;
                                    handleMessageClick(messageItem, false, { stopPropagation: () => {} });
                                }}
                            >
                                <div className="mailbox-card-checkbox-icon">
                                    <div className="mailbox-card-icon">
                                        <div className={`mailbox-icon-wrapper ${!messageItem.has_been_read ? 'unread' : ''}`}>
                                            {messageItem.item_type === 'document' ? <FileTextOutlined /> : messageItem.message_type === 'documenso' ? <FontAwesomeIcon icon={faSignature} /> : <MailOutlined />}
                                        </div>
                                    </div>
                                    <Checkbox
                                        checked={selectedMessages.includes(messageItem.unique_identifier)}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            const checked = e.target.checked;
                                            setSelectedMessages(prev => 
                                                checked 
                                                    ? [...prev, messageItem.unique_identifier] 
                                                    : prev.filter(id => id !== messageItem.unique_identifier)
                                            );
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="mailbox-card-checkbox"
                                    />
                                </div>
                                <div className="mailbox-card-content">
                                    <div className="mailbox-card-main">
                                        <div className="mailbox-card-left">
                                            <div className="mailbox-card-sender">
                                                {currentView === 'inbox' ? messageItem.sender : messageItem.recipient}
                                            </div>
                                            <div className="mailbox-card-subject">
                                                {messageItem.subject}
                                            </div>
                                            <div className="mailbox-card-preview">
                                                {stripHtmlTags(messageItem.body || messageItem.preview || '')}
                                            </div>
                                        </div>
                                        <div className="mailbox-card-right">
                                            <div className="mailbox-card-dates">
                                                {messageItem.created_at ? dayjs(messageItem.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                                {messageItem.ttlive && <span className="mailbox-card-date-separator"> / </span>}
                                                {messageItem.ttlive && dayjs(messageItem.ttlive).format('YYYY-MM-DD HH:mm')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {messageItem.item_type === 'document' && messageItem.document_files && Array.isArray(messageItem.document_files) && messageItem.document_files.length > 0 && (
                                    <div className="mailbox-card-doc-count">
                                        {messageItem.document_files.length} {messageItem.document_files.length === 1 ? t('mailbox.document') : t('mailbox.documents')}
                                    </div>
                                )}
                                {messageItem.item_type !== 'document' && messageItem.message_type !== 'documenso' && messageItem.files && Array.isArray(messageItem.files) && messageItem.files.length > 0 && (
                                    <div className="mailbox-card-doc-count">
                                        {messageItem.files.length} {messageItem.files.length === 1 ? t('mailbox.file') : t('mailbox.files')}
                                    </div>
                                )}
                                {messageItem.documenso_files && Array.isArray(messageItem.documenso_files) && messageItem.documenso_files.length > 0 && (
                                    <div className="mailbox-card-doc-count">
                                        {messageItem.documenso_files.length} {messageItem.documenso_files.length === 1 ? t('mailbox.file') : t('mailbox.files')}
                                    </div>
                                )}
                                </div>

                            {/* Replies are now shown in the preview panel instead of here */}
                            {false && hasRepliesFlag && item.replies && (
                                <div className="mailbox-thread-replies">
                                    {item.replies.map((reply) => (
                                        <div 
                                            key={reply.unique_identifier}
                                            className={`mailbox-card mailbox-card-reply ${selectedMessages.includes(reply.unique_identifier) ? 'mailbox-card-selected' : ''} ${previewMessage?.unique_identifier === reply.unique_identifier ? 'mailbox-card-preview-selected' : ''}`}
                                            data-item-type={reply.item_type || 'message'}
                                            onClick={(e) => {
                                                // Don't do anything if clicking checkbox or action buttons
                                                if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.mailbox-card-action-btn')) {
                                                    return;
                                                }
                                                
                                                // If Ctrl/Cmd is held, toggle checkbox selection
                                                if (e.ctrlKey || e.metaKey) {
                                                    e.stopPropagation();
                                                    setSelectedMessages(prev => 
                                                        prev.includes(reply.unique_identifier)
                                                            ? prev.filter(id => id !== reply.unique_identifier)
                                                            : [...prev, reply.unique_identifier]
                                                    );
                                                    return;
                                                }
                                                
                                                // Otherwise, set as preview
                                                setPreviewMessage(reply);
                                            }}
                                            onDoubleClick={(e) => {
                                                if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.mailbox-card-action-btn')) return;
                                                handleMessageClick(reply, true, { stopPropagation: () => {} });
                                            }}
                                        >
                                            <div className="mailbox-card-checkbox-icon">
                                                <div className="mailbox-card-icon">
                                                    <div className={`mailbox-icon-wrapper ${!reply.has_been_read ? 'unread' : ''}`}>
                                                        {reply.item_type === 'document' ? <FileTextOutlined /> : reply.message_type === 'documenso' ? <FontAwesomeIcon icon={faSignature} /> : <MailOutlined />}
                                                    </div>
                                                </div>
                                                <Checkbox
                                                    checked={selectedMessages.includes(reply.unique_identifier)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        const checked = e.target.checked;
                                                        setSelectedMessages(prev => 
                                                            checked 
                                                                ? [...prev, reply.unique_identifier] 
                                                                : prev.filter(id => id !== reply.unique_identifier)
                                                        );
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className="mailbox-card-content">
                                                <div className="mailbox-card-main">
                                                    <div className="mailbox-card-left">
                                                        <div className="mailbox-card-sender">{reply.sender}</div>
                                                        <div className="mailbox-card-subject">
                                                            {reply.subject}
                                                        </div>
                                                        <div className="mailbox-card-preview">
                                                            {stripHtmlTags(reply.body || reply.preview || '')}
                                                        </div>
                                                    </div>
                                                    <div className="mailbox-card-right">
                                                        <div className="mailbox-card-time">
                                                            <strong>
                                                                {currentView === 'inbox' ? t('mailbox.receivedAt') + ':' :
                                                                 currentView === 'sent' ? t('mailbox.sentAt') + ':' :
                                                                 currentView === 'deleted' ? t('mailbox.deletedAt') + ':' :
                                                                 currentView === 'drafts' ? t('mailbox.createdAt') + ':' :
                                                                 currentView === 'templates' ? t('mailbox.createdAt') + ':' :
                                                                 t('mailbox.createdAt') + ':'}
                                                            </strong>
                                                            {' '}
                                                            {reply.created_at ? dayjs(reply.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                                                        </div>
                                                        <div className="mailbox-card-expires">
                                                            {reply.ttlive ? `${t('mailbox.expiresAt')} ${dayjs(reply.ttlive).format('YYYY-MM-DD HH:mm')}` : '-'}
                                                        </div>
                                                        {reply.item_type === 'document' && reply.document_files && Array.isArray(reply.document_files) && reply.document_files.length > 0 && (
                                                            <div className="mailbox-card-doc-count">
                                                                {reply.document_files.length} {reply.document_files.length === 1 ? t('mailbox.document') : t('mailbox.documents')}
                                                            </div>
                                                        )}
                                                        {reply.item_type !== 'document' && reply.files && Array.isArray(reply.files) && reply.files.length > 0 && (
                                                            <div className="mailbox-card-doc-count">
                                                                {reply.files.length} {reply.files.length === 1 ? t('mailbox.file') : t('mailbox.files')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mailbox-card-actions">
                                                {(currentView === 'sent' || currentView === 'drafts' || currentView === 'templates') && (
                                                    <button
                                                        className="mailbox-card-action-btn mailbox-card-edit-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (currentView === 'drafts') {
                                                                handleEditDraft(reply.unique_identifier);
                                                            } else if (currentView === 'templates') {
                                                                handleEditTemplate(reply.unique_identifier);
                                                            } else if (currentView === 'sent') {
                                                                handleEdit(reply.unique_identifier);
                                                            }
                                                        }}
                                                        title={currentView === 'drafts' ? 'Edit Draft' : currentView === 'templates' ? 'Edit Template' : 'Edit'}
                                                    >
                                                        <EditOutlined />
                                                    </button>
                                                )}
                                                <button
                                                    className="mailbox-card-action-btn mailbox-card-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(reply.unique_identifier);
                                                    }}
                                                    title="Delete"
                                                >
                                                    <DeleteOutlined />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                {/* Blank spacer records so floating buttons don't overlap last folder record */}
                {sortedEmails.length > 0 && (
                    <>
                        <div className="mailbox-list-spacer" aria-hidden="true" />
                        <div className="mailbox-list-spacer" aria-hidden="true" />
                    </>
                )}
            </div>
        );
    };


    // Helper function to render document file status icons
    const renderDocumentFileIcons = (documentFiles) => {
        if (!documentFiles || !Array.isArray(documentFiles) || documentFiles.length === 0) {
            return null;
        }

        return (
            <div style={{ 
                display: 'flex', 
                gap: '12px', 
                alignItems: 'center',
                flexWrap: 'nowrap',
                padding: '8px 0'
            }}>
                {documentFiles.map((docFile, index) => {
                    let icon = null;
                    let statusText = '';
                    let bgColor = '';
                    
                    if (!docFile.document_name) {
                        // No document uploaded - question mark
                        icon = '?';
                        statusText = 'Pending Upload';
                        bgColor = '#e0e0e0';
                    } else {
                        // Document uploaded - show status
                        switch (docFile.document_status) {
                            case 'approved':
                                icon = '✓';
                                statusText = 'Approved';
                                bgColor = '#52c41a';
                                break;
                            case 'denied':
                                icon = '✗';
                                statusText = 'Denied';
                                bgColor = '#ff4d4f';
                                break;
                            case 'pending':
                            default:
                                icon = '?';
                                statusText = 'Pending Review';
                                bgColor = '#faad14';
                                break;
                        }
                    }

                    return (
                        <div 
                            key={index}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                flexShrink: 0,
                                minWidth: '100px'
                            }}
                        >
                            <Tooltip
                                title={
                                    docFile.document_status === 'denied' && docFile.document_denied_reason
                                        ? (
                                            <div>
                                                <div>{docFile.title}</div>
                                                <div style={{ color: '#ff4d4f', marginTop: '4px' }}>{docFile.document_denied_reason}</div>
                                            </div>
                                        )
                                        : docFile.title
                                }
                            >
                                <div
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        backgroundColor: bgColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}
                                >
                                    {icon}
                                </div>
                            </Tooltip>
                            <div style={{
                                fontSize: '12px',
                                color: '#595959',
                                fontWeight: 500,
                                maxWidth: '120px',
                                textAlign: 'center',
                                wordBreak: 'break-word'
                            }}>
                                {docFile.title}
                            </div>
                            <div style={{
                                fontSize: '11px',
                                color: '#8c8c8c',
                                textAlign: 'center'
                            }}>
                                {statusText}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Render preview panel
    const renderPreviewPanel = () => {
        if (!previewMessage) {
            return (
                <div className="mailbox-preview-empty">
                    <MailOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '16px' }} />
                    <h3>{t('mailbox.noMessageSelected')}</h3>
                    <p>{t('mailbox.selectMessageToView')}</p>
                </div>
            );
        }

        // Check if this message has replies (is a thread)
        const threadReplies = previewThread?.replies || [];
        const hasThreadReplies = threadReplies.length > 0;
        const allThreadMessagesForAttachments = hasThreadReplies ? [previewThread, ...threadReplies] : [];
        const threadAttachments = hasThreadReplies ? (() => {
            const items = [];
            allThreadMessagesForAttachments.forEach((msg) => {
                if (msg.item_type === 'document' && msg.document_files) {
                    (msg.document_files || []).filter((f) => f.document_name).forEach((doc) => {
                        items.push({ ...doc, source: 'document', displayName: doc.document_name });
                    });
                }
                if (msg.message_type === 'documenso' && msg.documenso_files) {
                    (msg.documenso_files || []).forEach((f) => {
                        items.push({ ...f, source: 'documenso', displayName: f.file_name || f.pdf_name });
                    });
                }
                if (msg.message_type !== 'documenso' && msg.item_type !== 'document' && msg.files) {
                    (msg.files || []).forEach((f) => {
                        items.push({ ...f, source: 'messages', displayName: f.file_name || f.pdf_name });
                    });
                }
            });
            return items;
        })() : [];

        return (
            <div className="mailbox-preview-content">
                {/* Actions bar at the top */}
                {previewMessage.access_link && (
                    <div className="mailbox-preview-actions">
                        <div>
                            {/* Sign button - recipient POV for signature requests - opens sign modal */}
                            {previewMessage.message_type === 'documenso' && previewMessage.sender !== user?.email && previewMessage.access_link && (previewMessage.documenso_files || []).some((f) => f.envelope_id) && (
                                <Button 
                                    type="primary" 
                                    icon={<FontAwesomeIcon icon={faSignature} />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDocumensoSignModalOpen(true);
                                    }}
                                    className="mailbox-preview-sign-btn"
                                >
                                    {t('documenso.signDocument', 'Sign')}
                                </Button>
                            )}
                            {/* Open button - always visible (or when not recipient for documenso) */}
                            <Button 
                                type={previewMessage.message_type === 'documenso' && previewMessage.sender !== user?.email ? 'default' : 'primary'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMessageClick(previewMessage, false, { stopPropagation: () => {} });
                                }}
                            >
                                {t('common.buttons.open')}
                            </Button>
                            
                            {/* Delete button for non-inbox folders */}
                            {currentView !== 'inbox' && (
                                <Button 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(previewMessage.unique_identifier);
                                    }}
                                />
                            )}
                            
                            {/* Edit button for non-inbox folders */}
                            {currentView !== 'inbox' && (
                                <Button 
                                    icon={<EditOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (currentView === 'drafts') {
                                            handleEditDraft(previewMessage.unique_identifier);
                                        } else if (currentView === 'templates') {
                                            handleEditTemplate(previewMessage.unique_identifier);
                                        } else if (currentView === 'sent' || currentView === 'deleted') {
                                            handleEdit(previewMessage.unique_identifier);
                                        }
                                    }}
                                />
                            )}
                        </div>
                        
                        {/* Right-side buttons: Download All (when files exist) + Send Documents (when receiver has uploaded) + Reply/Forward */}
                        <div>
                            {((previewMessage.files?.length > 0) || (previewMessage.documenso_files?.length > 0) || (previewMessage.item_type === 'document' && previewMessage.document_files?.filter(f => f.document_name).length > 0)) && (
                                <Tooltip title={t('messages.view.downloadAll')}>
                                    <Button 
                                        icon={<FolderOpenOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePreviewDownloadAll();
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {previewMessage.item_type === 'document' && previewMessage.sender !== user?.email && Object.keys(previewDocumentUploadedFiles).length > 0 && (
                                <Button 
                                    type="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePreviewSendDocuments();
                                    }}
                                >
                                    {t('documents.view.sendDocuments')}
                                </Button>
                            )}
                            {previewMessage.item_type !== 'document' && 
                             currentView !== 'drafts' && 
                             currentView !== 'templates' && 
                             currentView !== 'deleted' && (
                                <>
                                    {(previewMessage?.cc || hasMultipleRecipients(previewMessage?.recipient)) && !isViewerOnlyInBcc(previewMessage) && (
                                        <Button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleReplyAll(previewMessage);
                                            }}
                                            className="action-btn"
                                        >
                                            {t('messages.view.replyAll')}
                                        </Button>
                                    )}
                                    <Button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReply(previewMessage);
                                        }}
                                        className="action-btn"
                                    >
                                        {t('messages.view.reply')}
                                    </Button>
                                    {currentView !== 'inbox' && (
                                        <Button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleForward(previewMessage);
                                            }}
                                            className="action-btn"
                                        >
                                            {t('messages.view.forward')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                        
                    </div>
                )}
                
                {/* Header section - spans full width */}
                {currentView === 'templates' ? (
                    <div className="mailbox-preview-header-row mailbox-preview-header-row-template">
                        <div className="mailbox-preview-template-meta">
                            <span className="mailbox-preview-template-meta-item">
                                <CalendarOutlined className="mailbox-preview-template-meta-icon" />
                                {t('mailbox.createdAt') || 'Created'}: {previewMessage.created_at ? dayjs(previewMessage.created_at).format('MMM D, YYYY · h:mm A') : '-'}
                            </span>
                            <span className="mailbox-preview-template-meta-separator">·</span>
                            <span className="mailbox-preview-template-meta-item">
                                <ClockCircleOutlined className="mailbox-preview-template-meta-icon" />
                                {t('mailbox.expiryDate') || 'Expires'}: {previewMessage.ttlive ? dayjs(previewMessage.ttlive).format('MMM D, YYYY · h:mm A') : '-'}
                            </span>
                        </div>
                    </div>
                ) : (
                <div className="mailbox-preview-header-row">
                    <div className="mailbox-preview-header-left">
                        {currentView !== 'drafts' && currentView !== 'sent' && (
                            <div className="mailbox-preview-from">
                                <strong>{t('messages.view.from')}:</strong>{' '}
                                {previewMessage.sender || '-'}
                            </div>
                        )}
                        <div className={`mailbox-preview-to${!(previewMessage.recipient && String(previewMessage.recipient).trim()) ? ' mailbox-preview-field-empty' : ''}`}>
                            {(previewMessage.recipient && String(previewMessage.recipient).trim()) ? (
                                <><strong>{t('messages.view.to')}:</strong>{' '}{previewMessage.recipient}</>
                            ) : '\u00A0'}
                        </div>
                        <div className={`mailbox-preview-cc${
                            !(previewMessage.cc && String(previewMessage.cc).trim()) &&
                            !((currentView === 'sent' || currentView === 'drafts') && (previewMessage.bcc && String(previewMessage.bcc).trim()))
                                ? ' mailbox-preview-field-empty' : ''
                        }`}>
                            {(previewMessage.cc && String(previewMessage.cc).trim()) ? (
                                <><strong>{t('messages.view.cc')}:</strong>{' '}{previewMessage.cc}</>
                            ) : (
                                (currentView === 'sent' || currentView === 'drafts') && (previewMessage.bcc && String(previewMessage.bcc).trim())
                                    ? <><strong>{t('messages.view.cc')}:</strong>{' '}</>
                                    : '\u00A0'
                            )}
                        </div>
                        {(currentView === 'sent' || currentView === 'drafts') && (
                            <div className={`mailbox-preview-bcc${!(previewMessage.bcc && String(previewMessage.bcc).trim()) ? ' mailbox-preview-field-empty' : ''}`}>
                                {(previewMessage.bcc && String(previewMessage.bcc).trim()) ? (
                                    <><strong>{t('messagePreview.bcc')}</strong>{' '}{previewMessage.bcc}</>
                                ) : '\u00A0'}
                            </div>
                        )}
                    </div>
                    <div className="mailbox-preview-header-right">
                        <div className="mailbox-preview-created">
                            <span className="mailbox-preview-date-value">{previewMessage.created_at ? dayjs(previewMessage.created_at).format('MMMM D, YYYY h:mm A') : '-'}</span>
                            <CalendarOutlined className="mailbox-preview-icon" />
                        </div>
                        <div className="mailbox-preview-expires">
                            <span className="mailbox-preview-date-value">{previewMessage.ttlive ? dayjs(previewMessage.ttlive).format('MMMM D, YYYY h:mm A') : '-'}</span>
                            <ClockCircleOutlined className="mailbox-preview-icon" />
                        </div>
                    </div>
                </div>
                )}

                {/* Timeline for signature request - above subject, sender POV only */}
                {previewMessage.message_type === 'documenso' && previewMessage.sender === user?.email && previewMessage.documenso_files?.length > 0 && (
                    <div className="mailbox-preview-timeline-row">
                        <div className="documenso-preview-timeline documenso-preview-timeline-email mailbox-preview-documenso-timeline">
                            {[
                                { key: 'document_created_at', icon: FileAddOutlined, labelKey: 'documensoPreview.statusCreated' },
                                { key: 'document_sent_at', icon: SendOutlined, labelKey: 'documensoPreview.statusSent' },
                                { key: 'document_opened_at', icon: EyeOutlined, labelKey: 'documensoPreview.statusOpened' },
                                { key: 'document_signed_at', icon: EditOutlined, labelKey: 'documensoPreview.statusSigned' },
                                { key: 'document_completed_at', icon: CheckCircleOutlined, labelKey: 'documensoPreview.statusCompleted' },
                                { key: 'document_rejected_at', icon: CloseCircleOutlined, labelKey: 'documensoPreview.statusRejected' },
                                { key: 'document_cancelled_at', icon: StopOutlined, labelKey: 'documensoPreview.statusCancelled' },
                            ].map(({ key, icon: Icon, labelKey }, i) => {
                                const val = previewMessage[key];
                                const isDone = !!val;
                                const statusType = key === 'document_rejected_at' ? 'rejected' : key === 'document_cancelled_at' ? 'cancelled' : null;
                                const tooltipTitle = isDone ? t(labelKey) : undefined;
                                return (
                                    <React.Fragment key={key}>
                                        {i > 0 && <span className="documenso-preview-timeline-connector" />}
                                        <Tooltip title={tooltipTitle}>
                                            <span className={`documenso-preview-timeline-step ${isDone ? 'done' : 'pending'} ${statusType ? `status-${statusType}` : ''}`}>
                                                <Icon className="documenso-preview-timeline-icon" />
                                            </span>
                                        </Tooltip>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Thread attachments - one-line blocks above subject */}
                {hasThreadReplies && threadAttachments.length > 0 && (
                    <div className="mailbox-preview-thread-attachments">
                        {threadAttachments.map((file, index) => {
                            const isDoc = file.source === 'document';
                            const isDocumenso = file.source === 'documenso';
                            const displayName = file.displayName || (isDoc ? file.document_name : (file.file_name || file.pdf_name)) || (t('documensoPreview.documentPending') || 'Document pending');
                            const canPreview = isConvertible(displayName);
                            const commentCount = file.comment_count ?? 0;
                            const onPreview = () => setAttachmentPreviewDoc({
                                documentFileId: file.unique_identifier,
                                fileName: displayName,
                                source: file.source,
                            });
                            const onDownload = () => {
                                if (isDoc) handlePreviewDocumentDownload(file.unique_identifier, displayName);
                                else handlePreviewDownload(file.unique_identifier, displayName, isDocumenso);
                            };
                            return (
                                <div
                                    key={`${file.unique_identifier}-${index}`}
                                    className={`mailbox-preview-thread-attachment-block ${canPreview ? 'previewable' : ''}`}
                                    onClick={canPreview ? onPreview : undefined}
                                    role={canPreview ? 'button' : undefined}
                                    tabIndex={canPreview ? 0 : undefined}
                                    onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                >
                                    <span className="mailbox-preview-thread-attachment-name" title={displayName}>{displayName}</span>
                                    <span className="mailbox-preview-thread-attachment-actions">
                                        {canPreview && (
                                            <Button type="text" size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); onPreview(); }} />
                                        )}
                                        <Button type="text" size="small" icon={<DownloadOutlined />} onClick={(e) => { e.stopPropagation(); onDownload(); }} />
                                        {commentCount > 0 && (
                                            <span
                                                className="mailbox-preview-thread-attachment-comment"
                                                onClick={(e) => { e.stopPropagation(); if (canPreview) { onPreview(); } }}
                                                role={canPreview ? 'button' : undefined}
                                                tabIndex={canPreview ? 0 : undefined}
                                                onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } } : undefined}
                                            >
                                                <CommentOutlined />
                                                <span>{commentCount}</span>
                                            </span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Subject line */}
                <div className="mailbox-preview-subject">
                    {previewMessage.subject ?? ''}
                </div>

                {/* Body and documents/files row */}
                {(() => {
                    const hasDocumentFiles = previewMessage.item_type === 'document' && previewMessage.document_files && previewMessage.document_files.length > 0;
                    const hasMessageAttachments = (previewMessage.message_type === 'documenso' && previewMessage.documenso_files?.length > 0) ||
                        (previewMessage.message_type !== 'documenso' && previewMessage.files?.length > 0);
                    const hasRightColumn = hasThreadReplies ? false : (hasDocumentFiles || hasMessageAttachments);
                    return (
                <div 
                    className="mailbox-preview-content-row" 
                    ref={previewContentRef}
                    style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        gap: 0, 
                        flex: 1, 
                        minHeight: 0 
                    }}
                >
                    {/* Main preview area - body content */}
                    <div 
                        className="mailbox-preview-body-column"
                        style={{ 
                            width: hasRightColumn 
                                ? `${previewColumnWidths.body}%` 
                                : (hasThreadReplies ? undefined : '100%'),
                            flex: hasThreadReplies ? 1 : undefined,
                            display: 'flex', 
                            flexDirection: 'column', 
                            minWidth: 0, 
                            minHeight: 0 
                        }}
                    >
                        <div className="mailbox-preview-body">
                            <div dangerouslySetInnerHTML={{ __html: previewMessage.body || previewMessage.preview || '' }} />
                        </div>
                    </div>

                    {/* Resize Handle - for document requests or message attachments */}
                    {hasRightColumn && (
                        <div 
                            className="mailbox-preview-resize-handle"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizingPreview(true);
                            }}
                        />
                    )}

                    {/* Message attachments - right column (hidden when thread - shown in thread attachments above) */}
                    {hasMessageAttachments && !hasThreadReplies && (
                        <div 
                            className="mailbox-preview-attachments-column"
                            style={{ width: `${previewColumnWidths.documents}%`, minWidth: 200 }}
                        >
                            <div className="mailbox-preview-attachments-column-section">
                                <h4 className="mailbox-preview-attachments-column-title">
                                    {t('messages.view.attachments', { count: (previewMessage.message_type === 'documenso' ? (previewMessage.documenso_files || []) : (previewMessage.files || [])).length })}
                                </h4>
                                <div className="mailbox-preview-attachments-list mailbox-preview-attachments-list-rows">
                                    {previewMessage.message_type === 'documenso' && previewMessage.documenso_files.map((file, index) => {
                                        const hasFile = !!(file.file_name || file.pdf_name);
                                        const displayName = file.file_name || file.pdf_name;
                                        const fileSize = typeof file.file_size === 'number'
                                            ? `${(file.file_size / 1024).toFixed(2)} KB`
                                            : file.file_size;
                                        const isDocumensoDraft = previewMessage.folder === 'drafts' || previewMessage.folder === 'templates';
                                        const useSignaturePlacementModal = ['drafts', 'templates', 'sent'].includes(currentView);
                                        const setDocumensoPreview = () => {
                                            if (useSignaturePlacementModal) {
                                                setPlacementSelectedFile(isDocumensoDraft
                                                    ? { uid: file.unique_identifier, name: displayName, isDraftFile: true, download_url: `/api/drafts/files/${file.unique_identifier}/` }
                                                    : { uid: file.unique_identifier, name: displayName, documentFileId: file.unique_identifier, documentFileSource: 'documenso', isEditSentFile: true });
                                                setPlacementSelectedFileIndex(index);
                                                setPlacementModalOpen(true);
                                            } else if (isDocumensoDraft) {
                                                setAttachmentPreviewDoc({
                                                    draftFileId: file.unique_identifier,
                                                    draftDownloadUrl: `/api/drafts/files/${file.unique_identifier}/`,
                                                    fileName: displayName,
                                                    initialDraftComments: file.comments || [],
                                                });
                                            } else {
                                                setAttachmentPreviewDoc({
                                                    documentFileId: file.unique_identifier,
                                                    fileName: displayName,
                                                    source: 'documenso',
                                                });
                                            }
                                        };
                                        const documensoCommentCount = file.comment_count ?? (file.comments?.length ?? 0);
                                        return (
                                            <div key={file.unique_identifier || index} className="docpreview-file-row-wrapper">
                                                <div
                                                    className={`docpreview-file-row docpreview-file-row-documenso ${hasFile ? 'docpreview-file-row-previewable' : ''} ${hasFile && documensoCommentCount > 0 ? 'mailbox-preview-row-has-comments' : ''}`}
                                                    onClick={hasFile ? (e) => {
                                                        if (!e.target.closest('.docpreview-file-actions')) {
                                                            setDocumensoPreview();
                                                        }
                                                    } : undefined}
                                                    role={hasFile ? 'button' : undefined}
                                                    tabIndex={hasFile ? 0 : undefined}
                                                >
                                                    {hasFile && documensoCommentCount > 0 && (
                                                        <span
                                                            className="mailbox-preview-comment-badge"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDocumensoPreview();
                                                            }}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => {
                                                                if ((e.key === 'Enter' || e.key === ' ') && documensoCommentCount > 0) {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setDocumensoPreview();
                                                                }
                                                            }}
                                                            aria-label={`${documensoCommentCount} comment${documensoCommentCount !== 1 ? 's' : ''}`}
                                                        >
                                                            <CommentOutlined />
                                                            <span className="mailbox-preview-comment-badge-count">{documensoCommentCount}</span>
                                                        </span>
                                                    )}
                                                    <div className="docpreview-file-title-cell">
                                                        <span className="docpreview-file-title documenso-preview-file-title">
                                                            {displayName || (t('documensoPreview.documentPending') || 'Document pending')}
                                                        </span>
                                                        {fileSize && <span className="docpreview-file-size">{fileSize}</span>}
                                                    </div>
                                                        <div className="docpreview-file-actions documenso-preview-file-actions">
                                                            <div className="docpreview-file-preview">
                                                                {hasFile ? (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<EyeOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDocumensoPreview();
                                                                        }}
                                                                        className="docpreview-file-action-btn docpreview-file-preview-btn"
                                                                    />
                                                                ) : (
                                                                    <span className="docpreview-file-action-disabled"><EyeOutlined /></span>
                                                                )}
                                                            </div>
                                                            <div className="docpreview-file-download">
                                                            {hasFile ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<DownloadOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        isDocumensoDraft
                                                                            ? handlePreviewDraftFileDownload(file.unique_identifier, displayName)
                                                                            : handlePreviewDownload(file.unique_identifier, displayName, true);
                                                                    }}
                                                                    className="docpreview-file-action-btn docpreview-file-download-btn"
                                                                />
                                                            ) : (
                                                                <span className="docpreview-file-action-disabled"><DownloadOutlined /></span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {previewMessage.message_type !== 'documenso' && previewMessage.files.map((file, index) => {
                                        const fileName = file.file_name || file.pdf_name;
                                        const fileSize = file.file_size;
                                        const formattedSize = typeof fileSize === 'number'
                                            ? fileSize >= 1024 * 1024
                                                ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
                                                : `${(fileSize / 1024).toFixed(2)} KB`
                                            : fileSize || '-';
                                        const canPreview = isConvertible(fileName);
                                        const isDraftFile = previewMessage.folder === 'drafts' || previewMessage.folder === 'templates';
                                        const setPreview = () => isDraftFile
                                            ? setAttachmentPreviewDoc({
                                                draftFileId: file.unique_identifier,
                                                draftDownloadUrl: `/api/drafts/files/${file.unique_identifier}/`,
                                                fileName,
                                                initialDraftComments: Array.isArray(file.comments) ? file.comments : [],
                                            })
                                            : setAttachmentPreviewDoc({
                                                documentFileId: file.unique_identifier,
                                                fileName,
                                                source: 'messages',
                                            });
                                        const handleDownload = () => isDraftFile
                                            ? handlePreviewDraftFileDownload(file.unique_identifier, fileName)
                                            : handlePreviewDownload(file.unique_identifier, fileName, false);
                                        const commentCount = file.comment_count ?? (Array.isArray(file.comments) ? file.comments.length : 0);
                                        return (
                                            <div
                                                key={file.unique_identifier || index}
                                                className={`mailbox-preview-attachment-row ${canPreview ? 'mailbox-preview-attachment-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'mailbox-preview-row-has-comments' : ''}`}
                                                onClick={canPreview ? setPreview : undefined}
                                                role={canPreview ? 'button' : undefined}
                                                tabIndex={canPreview ? 0 : undefined}
                                                onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreview(); } } : undefined}
                                            >
                                                {canPreview && commentCount > 0 && (
                                                    <span
                                                        className="mailbox-preview-comment-badge"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setPreview();
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if ((e.key === 'Enter' || e.key === ' ') && commentCount > 0) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setPreview();
                                                            }
                                                        }}
                                                        aria-label={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                                                    >
                                                        <CommentOutlined />
                                                        <span className="mailbox-preview-comment-badge-count">{commentCount}</span>
                                                    </span>
                                                )}
                                                <div className="mailbox-preview-attachment-title-cell">
                                                    <span className="mailbox-preview-attachment-row-title">
                                                        {fileName}
                                                    </span>
                                                    <span className="mailbox-preview-attachment-row-filesize">
                                                        {formattedSize}
                                                    </span>
                                                </div>
                                                <div className="mailbox-preview-attachment-actions">
                                                    {canPreview && (
                                                        <Tooltip title={t('common.preview') || 'Preview'}>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                className="mailbox-preview-attachment-preview-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPreview();
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title={t('messages.view.download') || 'Download'}>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<DownloadOutlined />}
                                                            className="mailbox-preview-attachment-download-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownload();
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Document files list - only for document requests (hidden when thread - shown in thread attachments above) */}
                    {!hasThreadReplies && previewMessage.item_type === 'document' && previewMessage.document_files && previewMessage.document_files.length > 0 && (
                        <div 
                            className="mailbox-preview-documents-column"
                            style={{ width: `${previewColumnWidths.documents}%`, minWidth: 200 }}
                        >
                            <div className="mailbox-preview-documents-list-section">
                                <h4 className="mailbox-preview-documents-list-title">
                                    {t('documents.view.requestedDocuments', { count: previewMessage.document_files.length }) || `Requested Documents (${previewMessage.document_files.length})`}
                                </h4>
                                <div className="mailbox-preview-documents-list">
                                    {[...(previewMessage.document_files || [])]
                                        .sort((a, b) => (a.document_name || a.title || '').localeCompare(b.document_name || b.title || ''))
                                        .map((docFile, index) => {
                                        let statusText = '';
                                        let statusColor = '';
                                        const hasUploadedFile = !!docFile.document_name;
                                        const isReceiver = previewMessage.sender !== user?.email;
                                        const hasSelectedFile = !!previewDocumentUploadedFiles[docFile.unique_identifier];
                                        // Allow upload when: no document yet (receiver can upload), or document denied (receiver can replace). Block when approved or pending (uploaded, awaiting approval).
                                        const canUpload = isReceiver && docFile.document_status !== 'approved' && (!docFile.document_name || docFile.document_status !== 'pending');
                                        
                                        if (hasSelectedFile) {
                                            statusText = t('documents.view.readyToSend') || 'Ready to send';
                                            statusColor = 'blue';
                                        } else if (!docFile.document_name) {
                                            statusText = t('documents.view.statusNotSent') || 'NOT SENT';
                                            statusColor = 'default';
                                        } else {
                                            switch (docFile.document_status) {
                                                case 'approved':
                                                    statusText = t('documentPreview.statusApproved') || 'Approved';
                                                    statusColor = 'green';
                                                    break;
                                                case 'denied':
                                                    statusText = t('documentPreview.statusDenied') || 'Denied';
                                                    statusColor = 'red';
                                                    break;
                                                case 'pending':
                                                default:
                                                    statusText = t('documentPreview.statusPending') || 'Pending Review';
                                                    statusColor = 'orange';
                                                    break;
                                            }
                                        }

                                        const displayFileName = hasSelectedFile
                                            ? previewDocumentUploadedFiles[docFile.unique_identifier]?.name
                                            : (hasUploadedFile ? docFile.document_name : null);

                                        const canPreviewDoc = hasUploadedFile && isConvertible(docFile.document_name);

                                        const tooltipTitle = (
                                            <div>
                                                <div>Expected: {docFile.return_type ?? '*'}</div>
                                                {docFile.document_status === 'denied' && docFile.document_denied_reason && (
                                                    <div style={{ color: '#ff4d4f', marginTop: '4px' }}>{docFile.document_denied_reason}</div>
                                                )}
                                            </div>
                                        );

                                        const rowContent = (
                                            <>
                                                {canPreviewDoc && (docFile.comment_count ?? 0) > 0 && (
                                                    <span
                                                        className="mailbox-preview-comment-badge"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setAttachmentPreviewDoc({
                                                                documentFileId: docFile.unique_identifier,
                                                                fileName: docFile.document_name,
                                                                source: 'document',
                                                            });
                                                        }}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if ((e.key === 'Enter' || e.key === ' ') && (docFile.comment_count ?? 0) > 0) {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setAttachmentPreviewDoc({
                                                                    documentFileId: docFile.unique_identifier,
                                                                    fileName: docFile.document_name,
                                                                    source: 'document',
                                                                });
                                                            }
                                                        }}
                                                        aria-label={`${docFile.comment_count} comment${(docFile.comment_count ?? 0) !== 1 ? 's' : ''}`}
                                                    >
                                                        <CommentOutlined />
                                                        <span className="mailbox-preview-comment-badge-count">{docFile.comment_count}</span>
                                                    </span>
                                                )}
                                                <span className="mailbox-preview-document-title">
                                                    {docFile.title}
                                                    {displayFileName && (
                                                        <span className="mailbox-preview-document-uploaded-name"> : {displayFileName}</span>
                                                    )}
                                                </span>
                                                <div className="mailbox-preview-document-right">
                                                <div className="mailbox-preview-document-status-cell">
                                                    <span
                                                        className={`mailbox-preview-document-status mailbox-preview-document-status-${statusColor}`}
                                                    >
                                                        {statusText}
                                                    </span>
                                                </div>
                                                <div className="mailbox-preview-document-actions">
                                                    {isReceiver ? (
                                                        /* Recipient view - same icons as DocumentView: Preview, Upload, Remove */
                                                        <>
                                                            {/* Preview - enabled when document uploaded */}
                                                            {canPreviewDoc ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({
                                                                            documentFileId: docFile.unique_identifier,
                                                                            fileName: docFile.document_name,
                                                                            source: 'document',
                                                                        });
                                                                    }}
                                                                    className="mailbox-preview-document-preview-btn"
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <EyeOutlined />
                                                                </span>
                                                            )}
                                                            {/* Upload - enabled when canUpload (same logic as DocumentView) */}
                                                            {canUpload ? (
                                                                <span className="mailbox-preview-document-upload-icon">
                                                                    <UploadOutlined />
                                                                </span>
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <UploadOutlined />
                                                                </span>
                                                            )}
                                                            {/* Remove - enabled when hasSelectedFile (or has existing to replace - Mailbox only has selected) */}
                                                            {hasSelectedFile ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreviewRemoveFile(docFile.unique_identifier);
                                                                    }}
                                                                    className="mailbox-preview-document-remove-btn"
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <DeleteOutlined />
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        /* Sender view - same icons as DocumentPreview: Preview, Download, Approve, Deny */
                                                        <>
                                                            {/* Preview */}
                                                            {canPreviewDoc ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({
                                                                            documentFileId: docFile.unique_identifier,
                                                                            fileName: docFile.document_name,
                                                                            source: 'document',
                                                                        });
                                                                    }}
                                                                    className="mailbox-preview-document-preview-btn"
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <EyeOutlined />
                                                                </span>
                                                            )}
                                                            {/* Download */}
                                                            {hasUploadedFile ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<DownloadOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreviewDocumentDownload(docFile.unique_identifier, docFile.document_name);
                                                                    }}
                                                                    className="mailbox-preview-document-download-btn"
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <DownloadOutlined />
                                                                </span>
                                                            )}
                                                            {/* Approve */}
                                                            {hasUploadedFile && docFile.document_status !== 'approved' ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<CheckOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreviewApproveDocument(docFile.unique_identifier);
                                                                    }}
                                                                    className="mailbox-preview-document-approve-btn"
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <CheckOutlined />
                                                                </span>
                                                            )}
                                                            {/* Deny */}
                                                            {hasUploadedFile ? (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<CloseCircleOutlined />}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreviewDenyDocument(docFile.unique_identifier);
                                                                    }}
                                                                    className={`mailbox-preview-document-deny-btn ${docFile.document_status === 'denied' ? 'denied' : ''}`}
                                                                />
                                                            ) : (
                                                                <span className="mailbox-preview-document-action-disabled">
                                                                    <CloseCircleOutlined />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                </div>
                                            </>
                                        );

                                        // Recipient + denied: row click triggers upload (not preview). Otherwise, row click opens preview when document exists.
                                        const canPreviewFromRow = hasUploadedFile && !(isReceiver && docFile.document_status === 'denied') && isConvertible(docFile.document_name);
                                        const handleMailboxRowClick = (e) => {
                                            if (!canPreviewFromRow) return;
                                            if (e.target.closest('.mailbox-preview-document-actions')) return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setAttachmentPreviewDoc({
                                                documentFileId: docFile.unique_identifier,
                                                fileName: docFile.document_name,
                                                source: 'document',
                                            });
                                        };
                                        const rowNode = canUpload && !hasSelectedFile ? (
                                            <Upload
                                                accept={docFile.return_type === '*' ? '*' : docFile.return_type}
                                                beforeUpload={(file) => handlePreviewUploadDocument(file, docFile)}
                                                showUploadList={false}
                                            >
                                                <div
                                                    className={`mailbox-preview-document-row mailbox-preview-document-row-clickable ${canPreviewFromRow ? 'mailbox-preview-document-row-previewable' : ''} ${hasUploadedFile && (docFile.comment_count ?? 0) > 0 ? 'mailbox-preview-document-row-has-comments' : ''}`}
                                                    onClick={handleMailboxRowClick}
                                                >
                                                    {rowContent}
                                                </div>
                                            </Upload>
                                        ) : (
                                            <div
                                                className={`mailbox-preview-document-row mailbox-preview-document-row-actions ${canPreviewDoc ? 'mailbox-preview-document-row-previewable' : ''} ${canPreviewDoc && (docFile.comment_count ?? 0) > 0 ? 'mailbox-preview-document-row-has-comments' : ''}`}
                                                onClick={canPreviewDoc ? (e) => {
                                                    if (!e.target.closest('.mailbox-preview-document-actions')) {
                                                        setAttachmentPreviewDoc({
                                                            documentFileId: docFile.unique_identifier,
                                                            fileName: docFile.document_name,
                                                            source: 'document',
                                                        });
                                                    }
                                                } : undefined}
                                            >
                                                {rowContent}
                                            </div>
                                        );

                                        return (
                                            <div key={index} className="mailbox-preview-document-row-wrapper">
                                                <div className="mailbox-preview-document-item">
                                                    <Tooltip title={tooltipTitle} placement="top">
                                                        {rowNode}
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Thread list on the right - only for threaded messages */}
                    {hasThreadReplies && previewThread && (() => {
                        const allThreadMessages = [previewThread, ...threadReplies];
                        const totalCount = allThreadMessages.length;
                        return (
                            <>
                            <div
                                className="mailbox-preview-thread-resize-handle"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsResizingThread(true);
                                }}
                            />
                            <div
                                className="mailbox-preview-replies"
                                style={{ flex: `0 0 ${threadWidthPercent}%`, minWidth: 180 }}
                            >
                                <div className="mailbox-preview-replies-header">
                                    {t('mailbox.thread')} ({totalCount})
                                </div>
                                <div className="mailbox-preview-replies-list">
                                    {allThreadMessages.map((msg, index) => (
                                        <div
                                            key={msg.unique_identifier}
                                            className={`mailbox-preview-reply-item ${previewMessage.unique_identifier === msg.unique_identifier ? 'active' : ''} ${index === 0 ? 'mailbox-preview-reply-original' : ''}`}
                                            onClick={() => setPreviewMessage(msg)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewMessage(msg); } }}
                                            aria-label={`${msg.sender || ''} - ${msg.subject || ''}`}
                                        >
                                            <div className="mailbox-preview-reply-sender">{msg.sender || '-'}</div>
                                            <div className="mailbox-preview-reply-subject">{msg.subject ?? ''}</div>
                                            <div className="mailbox-preview-reply-date">
                                                {msg.created_at ? dayjs(msg.created_at).format('MMM D, h:mm A') : '-'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            </>
                        );
                    })()}
                </div>
                    );
                })()}
            </div>
        );
    };

    // Get counts for section badges based on current folder
    const getSectionCounts = () => {
        if (!dataLoaded) return { all: 0, messages: 0, documents: 0, signature_requests: 0 };
        
        let messagesCount = 0;
        let documentsCount = 0;
        let signatureRequestsCount = 0;
        
        const countSignatureRequests = (items) => (items || []).filter(
            item => item.item_type !== 'document' && item.message_type === 'documenso'
        ).length;
        
        const excludeDocumenso = (items) => (items || []).filter(item => item.message_type !== 'documenso');
        if (currentView === 'deleted') {
            const deletedData = allFolderData.deleted || [];
            messagesCount = excludeDocumenso(deletedData).filter(item => item.item_type === 'message' || !item.item_type).length;
            documentsCount = deletedData.filter(item => item.item_type === 'document').length;
            signatureRequestsCount = countSignatureRequests(deletedData);
        } else if (currentView === 'inbox') {
            const inboxData = allFolderData.inbox || [];
            messagesCount = excludeDocumenso(inboxData).length;
            const inboxDocsData = allFolderData.inbox_documents || [];
            documentsCount = inboxDocsData.length;
            signatureRequestsCount = countSignatureRequests(inboxData);
        } else if (currentView === 'sent') {
            const sentData = allFolderData.sent || [];
            messagesCount = excludeDocumenso(sentData).length;
            const sentDocsData = allFolderData.sent_documents || [];
            documentsCount = sentDocsData.length;
            signatureRequestsCount = countSignatureRequests(sentData);
        } else if (currentView === 'drafts') {
            const draftsData = allFolderData.drafts || [];
            messagesCount = excludeDocumenso(draftsData).length;
            const draftsDocsData = allFolderData.drafts_documents || [];
            documentsCount = draftsDocsData.length;
            signatureRequestsCount = countSignatureRequests(draftsData);
        } else if (currentView === 'templates') {
            const templatesData = allFolderData.templates || [];
            messagesCount = excludeDocumenso(templatesData).length;
            const templatesDocsData = allFolderData.templates_documents || [];
            documentsCount = templatesDocsData.length;
            signatureRequestsCount = countSignatureRequests(templatesData);
        }
        
        return {
            all: messagesCount + documentsCount,
            messages: messagesCount,
            documents: documentsCount,
            signature_requests: signatureRequestsCount
        };
    };

    // Get folder badge counts - unread for inbox, total for others
    const getFolderBadgeCount = (folderKey) => {
        if (!dataLoaded) return 0;
        
        if (folderKey === 'inbox') {
            // For inbox, count unread items (messages + documents)
            let unreadCount = 0;
            const inboxData = allFolderData.inbox || [];
            inboxData.forEach(thread => {
                if (thread && !thread.has_been_read) {
                    unreadCount++;
                }
                if (thread && thread.replies && Array.isArray(thread.replies)) {
                    thread.replies.forEach(reply => {
                        if (!reply.has_been_read) {
                            unreadCount++;
                        }
                    });
                }
            });
            const inboxDocsData = allFolderData.inbox_documents || [];
            unreadCount += inboxDocsData.filter(doc => !doc.has_been_read).length;
            return unreadCount;
        } else if (folderKey === 'deleted') {
            // For deleted folder, all items are in a single array
            const deletedData = allFolderData.deleted || [];
            return deletedData.length;
        } else {
            // For other folders, count all items (messages + documents)
            const folderData = allFolderData[folderKey] || [];
            const folderDocsKey = `${folderKey}_documents`;
            const folderDocsData = allFolderData[folderDocsKey] || [];
            return folderData.length + folderDocsData.length;
        }
    };

    return (
        <>
            <Layout className="mailbox-layout">
                <Content className="mailbox-content">
                    {/* Top bar - folders + section toggles, action buttons when selected */}
                    <div className="mailbox-top-bar">
                        <div className="mailbox-top-folders">
                            <div className="mailbox-section-toggles">
                                <Button
                                    type="text"
                                    icon={<MessageOutlined />}
                                    onClick={() => handleSectionChange(sectionView === 'messages' ? 'all' : 'messages')}
                                    className={`mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'messages' ? 'active' : ''}`}
                                    title={t('mailbox.messages')}
                                    aria-label={t('mailbox.messages')}
                                />
                                <Button
                                    type="text"
                                    icon={<FileTextOutlined />}
                                    onClick={() => handleSectionChange(sectionView === 'documents' ? 'all' : 'documents')}
                                    className={`mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'documents' ? 'active' : ''}`}
                                    title={t('mailbox.documents')}
                                    aria-label={t('mailbox.documents')}
                                />
                                <Button
                                    type="text"
                                    icon={<FormOutlined />}
                                    onClick={() => handleSectionChange(sectionView === 'signature_requests' ? 'all' : 'signature_requests')}
                                    className={`mailbox-section-toggle-btn ${sectionView === 'all' || sectionView === 'signature_requests' ? 'active' : ''}`}
                                    title={t('mailbox.signatureRequests')}
                                    aria-label={t('mailbox.signatureRequests')}
                                />
                            </div>
                            <div className="mailbox-folder-buttons">
                                <Button
                                    type={currentView === 'inbox' ? 'primary' : 'default'}
                                    onClick={() => {
                                        handleViewChange('inbox');
                                        navigate('/profile/mailbox?folder=inbox');
                                    }}
                                    className={`mailbox-folder-btn ${currentView === 'inbox' ? 'active' : ''}`}
                                >
                                    <span className="mailbox-folder-btn-label">
                                        {t('mailbox.inbox')}
                                        <Badge count={getFolderBadgeCount('inbox')} showZero style={{ marginLeft: 6, boxShadow: 'none' }} />
                                    </span>
                                </Button>
                                <Button
                                    type={currentView === 'sent' ? 'primary' : 'default'}
                                    onClick={() => {
                                        handleViewChange('sent');
                                        navigate('/profile/mailbox?folder=sent');
                                    }}
                                    className={`mailbox-folder-btn ${currentView === 'sent' ? 'active' : ''}`}
                                >
                                    <span className="mailbox-folder-btn-label">
                                        {t('mailbox.sent')}
                                        <Badge count={getFolderBadgeCount('sent')} showZero style={{ marginLeft: 6, boxShadow: 'none' }} />
                                    </span>
                                </Button>
                                <Button
                                    type={currentView === 'drafts' ? 'primary' : 'default'}
                                    onClick={() => {
                                        handleViewChange('drafts');
                                        navigate('/profile/mailbox?folder=drafts');
                                    }}
                                    className={`mailbox-folder-btn ${currentView === 'drafts' ? 'active' : ''}`}
                                >
                                    <span className="mailbox-folder-btn-label">
                                        {t('mailbox.drafts')}
                                        <Badge count={getFolderBadgeCount('drafts')} showZero style={{ marginLeft: 6, boxShadow: 'none' }} />
                                    </span>
                                </Button>
                                <Button
                                    type={currentView === 'templates' ? 'primary' : 'default'}
                                    onClick={() => {
                                        handleViewChange('templates');
                                        navigate('/profile/mailbox?folder=templates');
                                    }}
                                    className={`mailbox-folder-btn ${currentView === 'templates' ? 'active' : ''}`}
                                >
                                    <span className="mailbox-folder-btn-label">
                                        {t('mailbox.templates')}
                                        <Badge count={getFolderBadgeCount('templates')} showZero style={{ marginLeft: 6, boxShadow: 'none' }} />
                                    </span>
                                </Button>
                                <Button
                                    type={currentView === 'deleted' ? 'primary' : 'default'}
                                    onClick={() => {
                                        handleViewChange('deleted');
                                        navigate('/profile/mailbox?folder=deleted');
                                    }}
                                    className={`mailbox-folder-btn ${currentView === 'deleted' ? 'active' : ''}`}
                                >
                                    <span className="mailbox-folder-btn-label">
                                        {t('mailbox.deleted')}
                                        <Badge count={getFolderBadgeCount('deleted')} showZero style={{ marginLeft: 6, boxShadow: 'none' }} />
                                    </span>
                                </Button>
                            </div>
                        </div>
                        <div className="mailbox-top-actions">
                            {selectedMessages.length > 0 && (
                                <>
                                    {currentView === 'deleted' && (
                                        <Button
                                            type="primary"
                                            icon={<InboxOutlined />}
                                            onClick={() => handleRecover()}
                                        >
                                            {t('mailbox.recoverSelected')}
                                        </Button>
                                    )}
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => handleDelete()}
                                    >
                                        {currentView === 'deleted' ? t('mailbox.permanentDelete') : t('mailbox.delete')}
                                    </Button>
                                </>
                            )}
                            {selectedMessages.length === 0 && previewMessage && currentView !== 'inbox' && (
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDelete(previewMessage.unique_identifier)}
                                >
                                    {currentView === 'deleted' ? t('mailbox.permanentDelete') : t('mailbox.delete')}
                                </Button>
                            )}
                            {user?.email && (
                                <span className="mailbox-user-email">{user.email}</span>
                            )}
                        </div>
                    </div>

                    <div className="mailbox-main-container" ref={containerRef}>
                        <div 
                            className="mailbox-list-container"
                            style={{ flex: `0 0 ${listWidth}%` }}
                        >
                            {/* Search bar and sort dropdown */}
                            <div className="mailbox-search-section-wrapper">
                            <div className={`mailbox-search-sort-container ${isSearchSectionCollapsed ? 'collapsed' : ''}`}>
                                <Checkbox
                                    className="mailbox-select-all-checkbox"
                                    checked={isAllSelected}
                                    indeterminate={isIndeterminate}
                                    onChange={handleSelectAll}
                                />
                                <div className="mailbox-search">
                                    <Input
                                        placeholder={t('mailbox.search')}
                                        prefix={<SearchOutlined />}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onFocus={() => setIsSearchFocused(true)}
                                        onBlur={() => setIsSearchFocused(false)}
                                        allowClear
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                </div>
                                <div className="mailbox-sort-dropdown">
                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'created',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <CalendarOutlined style={{ marginRight: '8px' }} />
                                                                {currentView === 'inbox' ? t('mailbox.receivedAt') || 'Received At' :
                                                                 currentView === 'sent' ? t('mailbox.sentAt') || 'Sent At' :
                                                                 currentView === 'deleted' ? t('mailbox.deletedAt') || 'Deleted At' :
                                                                 currentView === 'drafts' ? t('mailbox.createdAt') || 'Created At' :
                                                                 currentView === 'templates' ? t('mailbox.createdAt') || 'Created At' :
                                                                 t('mailbox.dateCreated') || 'Date Created'}
                                                            </span>
                                                            {sortConfig.key === 'created' && (
                                                                sortConfig.direction === 'ascend' ? '↑' : '↓'
                                                            )}
                                                        </div>
                                                    ),
                                                    onClick: () => {
                                                        setSortConfig({
                                                            key: 'created',
                                                            direction: sortConfig.key === 'created' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend'
                                                        });
                                                    }
                                                },
                                                {
                                                    key: 'expires',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <ClockCircleOutlined style={{ marginRight: '8px' }} />
                                                                {t('mailbox.expiryDate') || 'Expiry Date'}
                                                            </span>
                                                            {sortConfig.key === 'expires' && (
                                                                sortConfig.direction === 'ascend' ? '↑' : '↓'
                                                            )}
                                                        </div>
                                                    ),
                                                    onClick: () => {
                                                        setSortConfig({
                                                            key: 'expires',
                                                            direction: sortConfig.key === 'expires' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend'
                                                        });
                                                    }
                                                },
                                                {
                                                    key: 'subject',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <FileTextOutlined style={{ marginRight: '8px' }} />
                                                                {t('mailbox.subject') || 'Subject'}
                                                            </span>
                                                            {sortConfig.key === 'subject' && (
                                                                sortConfig.direction === 'ascend' ? '↑' : '↓'
                                                            )}
                                                        </div>
                                                    ),
                                                    onClick: () => {
                                                        setSortConfig({
                                                            key: 'subject',
                                                            direction: sortConfig.key === 'subject' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend'
                                                        });
                                                    }
                                                },
                                                {
                                                    key: 'read',
                                                    label: (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <span>
                                                                <EyeOutlined style={{ marginRight: '8px' }} />
                                                                {t('mailbox.readStatus') || 'Read Status'}
                                                            </span>
                                                            {sortConfig.key === 'read' && (
                                                                sortConfig.direction === 'ascend' ? '↑' : '↓'
                                                            )}
                                                        </div>
                                                    ),
                                                    onClick: () => {
                                                        setSortConfig({
                                                            key: 'read',
                                                            direction: sortConfig.key === 'read' && sortConfig.direction === 'ascend' ? 'descend' : 'ascend'
                                                        });
                                                    }
                                                },
                                            ]
                                        }}
                                        trigger={['click']}
                                    >
                                        <Button style={{ width: '100%' }}>
                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                <span>
                                                    {sortConfig.key === null ? (
                                                        t('mailbox.sortBy') || 'Sort By'
                                                    ) : sortConfig.key === 'created' ? (
                                                        <>
                                                            {currentView === 'inbox' ? (t('mailbox.receivedAt') || 'Received At') :
                                                             currentView === 'sent' ? (t('mailbox.sentAt') || 'Sent At') :
                                                             currentView === 'deleted' ? (t('mailbox.deletedAt') || 'Deleted At') :
                                                             (t('mailbox.createdAt') || 'Created At')}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : sortConfig.key === 'expires' ? (
                                                        <>
                                                            {t('mailbox.expiryDate') || 'Expiry Date'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : sortConfig.key === 'subject' ? (
                                                        <>
                                                            {t('mailbox.subject') || 'Subject'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : sortConfig.key === 'read' ? (
                                                        <>
                                                            {t('mailbox.readStatus') || 'Read Status'}
                                                            {' '}
                                                            {sortConfig.direction === 'ascend' ? '↑' : '↓'}
                                                        </>
                                                    ) : (
                                                        t('mailbox.sortBy') || 'Sort By'
                                                    )}
                                                </span>
                                                <DownOutlined />
                                            </Space>
                                        </Button>
                                    </Dropdown>
                                </div>
                            </div>
                            <div 
                                className={`mailbox-collapse-chevron ${isSearchSectionCollapsed ? 'collapsed' : ''}`}
                                onClick={() => setIsSearchSectionCollapsed(!isSearchSectionCollapsed)}
                                aria-label={isSearchSectionCollapsed ? 'Expand search' : 'Collapse search'}
                            >
                                <div className="mailbox-collapse-chevron-line" />
                                <div className="mailbox-collapse-chevron-icon">
                                    {isSearchSectionCollapsed ? <UpOutlined /> : <DownOutlined />}
                                </div>
                                <div className="mailbox-collapse-chevron-line" />
                            </div>
                            </div>
                            {/* Scrollable content area */}
                            <div className="mailbox-list-scrollable">
                                {renderMailContent()}
                            </div>
                            {/* Compose FAB - bottom right of records section */}
                            <div className="mailbox-compose-fab-wrapper">
                                <Tooltip title={t('mailbox.compose')} placement="left">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setComposeModalVisible(true)}
                                        className="mailbox-compose-fab"
                                        aria-label={t('mailbox.compose')}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        {/* Compose modal - choose action */}
                        <Modal
                            title={t('mailbox.composeModalTitle')}
                            open={composeModalVisible}
                            onCancel={() => setComposeModalVisible(false)}
                            footer={null}
                            className="mailbox-compose-modal"
                        >
                            <div className="mailbox-compose-options">
                                <div
                                    className="mailbox-compose-option"
                                    onClick={() => {
                                        setComposeModalVisible(false);
                                        localStorage.removeItem('currentDraftId');
                                        navigate('/message-compose');
                                    }}
                                >
                                    <div className="mailbox-compose-option-icon" style={{ background: '#e6f7ff', color: '#1890ff' }}>
                                        <MessageOutlined />
                                    </div>
                                    <div className="mailbox-compose-option-content">
                                        <h3>{t('mailbox.composeMessage')}</h3>
                                    </div>
                                </div>
                                <div
                                    className="mailbox-compose-option"
                                    onClick={() => {
                                        setComposeModalVisible(false);
                                        navigate('/document-compose');
                                    }}
                                >
                                    <div className="mailbox-compose-option-icon" style={{ background: '#f6ffed', color: '#52c41a' }}>
                                        <FileAddOutlined />
                                    </div>
                                    <div className="mailbox-compose-option-content">
                                        <h3>{t('mailbox.requestDocuments')}</h3>
                                    </div>
                                </div>
                                <div
                                    className="mailbox-compose-option"
                                    onClick={() => {
                                        setComposeModalVisible(false);
                                        localStorage.removeItem('currentDraftId');
                                        navigate('/signature-request');
                                    }}
                                >
                                    <div className="mailbox-compose-option-icon" style={{ background: '#f0e6ff', color: '#5E5BFF' }}>
                                        <FormOutlined />
                                    </div>
                                    <div className="mailbox-compose-option-content">
                                        <h3>{t('mailbox.signatureRequest')}</h3>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                        <div 
                            className="mailbox-resize-handle-vertical"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizingPanels(true);
                            }}
                        />
                        <div 
                            className="mailbox-preview-panel"
                            style={{ flex: `0 0 ${100 - listWidth}%` }}
                        >
                            {renderPreviewPanel()}
                        </div>
                    </div>
                </Content>
            </Layout>

            <FooterComponent />

            {/* Document deny modal (for document request preview) */}
            <Modal
                title={
                    <Space>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        {t('documentPreview.rejectingDocument', { documentName: previewMessage?.document_files?.find(f => f.unique_identifier === previewDenyingFileId)?.title || 'document' })}
                    </Space>
                }
                open={previewDenyModalVisible}
                onOk={handlePreviewConfirmDeny}
                onCancel={handlePreviewCancelDeny}
                okText={t('documentPreview.confirmDeny')}
                cancelText={t('documentPreview.cancel')}
                okButtonProps={{ danger: true }}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>{t('documentPreview.denyReasonPrompt')}</Text>
                </div>
                <Input.TextArea
                    value={previewDenyReason}
                    onChange={(e) => setPreviewDenyReason(e.target.value)}
                    placeholder={t('documentPreview.denyReasonPlaceholder')}
                    rows={4}
                    maxLength={500}
                />
            </Modal>

            <SignaturePlacementModal
                open={placementModalOpen}
                onClose={async () => {
                    if (saveSignatureFieldsTimeoutRef.current) {
                        clearTimeout(saveSignatureFieldsTimeoutRef.current);
                        saveSignatureFieldsTimeoutRef.current = null;
                    }
                    if (previewMessage && currentView === 'sent' && previewMessage.message_type === 'documenso') {
                        const sf = signaturePlacementValueRef.current?.signatureFields;
                        if (sf && Object.keys(sf).length > 0) {
                            try {
                                await axios.post(`/api/docuso/update_signature_fields/${previewMessage.unique_identifier}/`, {
                                    signature_fields: sf,
                                }, { headers: { 'Content-Type': 'application/json' } });
                                message.success(t('documenso.signatureFieldsUpdated') || 'Signature fields updated');
                                refreshMailbox();
                            } catch (err) {
                                message.error(t('messages.errors.saveFailed'));
                            }
                        }
                    } else if (previewMessage && (previewMessage.folder === 'drafts' || previewMessage.folder === 'templates') && previewMessage.message_type === 'documenso') {
                        saveDraftCommentsFromMailbox(draftFileCommentsRef.current, signaturePlacementValueRef.current?.signatureFields);
                        refreshMailbox();
                    }
                    setPlacementModalOpen(false);
                    setPlacementSelectedFile(null);
                    setSignaturePlacementValue({});
                }}
                file={placementSelectedFile}
                fileIndex={placementSelectedFileIndex}
                signerEmails={(previewMessage?.recipient || '').split(',').map((e) => e.trim()).filter(Boolean)}
                value={signaturePlacementValue}
                onChange={handleSignaturePlacementChange}
                onAddRecipient={() => false}
                disabled={false}
                draftFileComments={placementSelectedFile?.isDraftFile && placementSelectedFile?.uid ? draftFileComments[placementSelectedFile.uid] : undefined}
                onDraftFileCommentsChange={placementSelectedFile?.isDraftFile && placementSelectedFile?.uid ? (comments) => {
                    const fid = placementSelectedFile.uid;
                    latestDraftCommentsForSaveRef.current = { ...latestDraftCommentsForSaveRef.current, [fid]: comments };
                    setDraftFileComments((prev) => ({ ...prev, [fid]: comments }));
                    if (saveDraftCommentsTimeoutRef.current) clearTimeout(saveDraftCommentsTimeoutRef.current);
                    saveDraftCommentsTimeoutRef.current = setTimeout(() => {
                        const toSave = { ...draftFileCommentsRef.current, [fid]: comments };
                        saveDraftCommentsFromMailbox(toSave, signaturePlacementValueRef.current?.signatureFields);
                        saveDraftCommentsTimeoutRef.current = null;
                    }, 500);
                } : undefined}
                documentFileId={placementSelectedFile?.isEditSentFile && placementSelectedFile?.documentFileId ? placementSelectedFile.documentFileId : undefined}
                documentFileSource={placementSelectedFile?.isEditSentFile ? 'documenso' : undefined}
                documentFileComments={placementSelectedFile?.isEditSentFile && placementSelectedFile?.uid ? editSentFileComments[placementSelectedFile.uid] : undefined}
                onDocumentFileCommentsChange={placementSelectedFile?.isEditSentFile && placementSelectedFile?.uid ? (comments) => setEditSentFileComments((prev) => ({ ...prev, [placementSelectedFile.uid]: comments })) : undefined}
            />

            <DocumensoSignModal
                open={documensoSignModalOpen}
                onClose={() => setDocumensoSignModalOpen(false)}
                linkId={previewMessage ? getReplyIdentifier(previewMessage) : null}
                documensoFiles={previewMessage?.documenso_files || []}
                viewerEmail={user?.email}
                onSuccess={() => refreshMailbox()}
            />

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => {
                    if (saveDraftCommentsTimeoutRef.current) {
                        clearTimeout(saveDraftCommentsTimeoutRef.current);
                        saveDraftCommentsFromMailbox(draftFileCommentsRef.current);
                        saveDraftCommentsTimeoutRef.current = null;
                    }
                    setAttachmentPreviewDoc(null);
                }}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                source={attachmentPreviewDoc?.source || 'document'}
                draftFileId={attachmentPreviewDoc?.draftFileId}
                draftDownloadUrl={attachmentPreviewDoc?.draftDownloadUrl}
                draftFileComments={attachmentPreviewDoc?.draftFileId ? (draftFileComments[attachmentPreviewDoc.draftFileId] ?? getCommentsFromPreviewMessage(previewMessage, attachmentPreviewDoc.draftFileId) ?? attachmentPreviewDoc.initialDraftComments ?? EMPTY_ARRAY) : undefined}
                onDraftFileCommentsChange={attachmentPreviewDoc?.draftFileId ? handleDraftFileCommentsChangeFromMailbox : undefined}
                onDownload={attachmentPreviewDoc ? (attachmentPreviewDoc.draftFileId && attachmentPreviewDoc.draftDownloadUrl
                    ? () => handlePreviewDraftFileDownload(attachmentPreviewDoc.draftFileId, attachmentPreviewDoc.fileName)
                    : attachmentPreviewDoc.source === 'documenso'
                    ? () => handlePreviewDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName, true)
                    : attachmentPreviewDoc.source === 'messages'
                    ? () => handlePreviewDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName, false)
                    : () => handlePreviewDocumentDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName)
                ) : undefined}
            />
        </>
    );
};

export default Mailbox;